import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { doc, getDoc, setDoc, onSnapshot, collection, deleteDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { 
  CheckCircle2, User, LayoutDashboard, AlertCircle, 
  Settings, UploadCloud, FileText, Briefcase, Edit3, Plus, X, File, 
  Users, ChevronDown, Folder, Download, Trash2, CreditCard, ExternalLink, 
  FolderPlus, ArrowLeft, DollarSign, Check, Save, UserPlus, Filter, 
  Image as ImageIcon, History, Calendar, Archive, ChevronRight, 
  MessageSquare, Send, RotateCcw, Moon, Sun, AlertTriangle, Paperclip,
  Target, ArrowRight, Lock, LogOut, Menu, Mail, Clock
} from 'lucide-react';

// ==========================================
// 1. DATA HELPERS & MOCK DATA
// ==========================================
const initialClients = {};

const utcOffsets = [
  { label: "UTC-12 (Baker Island)", value: -12 },
  { label: "UTC-11 (Samoa)", value: -11 },
  { label: "UTC-10 (Hawaii)", value: -10 },
  { label: "UTC-9 (Alaska)", value: -9 },
  { label: "UTC-8 (Pacific Time)", value: -8 },
  { label: "UTC-7 (Mountain Time)", value: -7 },
  { label: "UTC-6 (Central Time)", value: -6 },
  { label: "UTC-5 (Eastern Time)", value: -5 },
  { label: "UTC-4 (Atlantic Time)", value: -4 },
  { label: "UTC-3 (Argentina, Brazil)", value: -3 },
  { label: "UTC-2 (Mid-Atlantic)", value: -2 },
  { label: "UTC-1 (Azores)", value: -1 },
  { label: "UTC+0 (London, GMT)", value: 0 },
  { label: "UTC+1 (Central Europe)", value: 1 },
  { label: "UTC+2 (Eastern Europe)", value: 2 },
  { label: "UTC+3 (Moscow, Arabia)", value: 3 },
  { label: "UTC+4 (Gulf Standard)", value: 4 },
  { label: "UTC+5 (Pakistan)", value: 5 },
  { label: "UTC+5.5 (India)", value: 5.5 },
  { label: "UTC+6 (Bangladesh)", value: 6 },
  { label: "UTC+7 (SE Asia)", value: 7 },
  { label: "UTC+8 (China, WAust)", value: 8 },
  { label: "UTC+9 (Japan, Korea)", value: 9 },
  { label: "UTC+10 (AEST)", value: 10 },
  { label: "UTC+11 (Solomon Is)", value: 11 },
  { label: "UTC+12 (New Zealand)", value: 12 }
];

const hoursList = Array.from({length: 24}, (_, i) => i);

const convertToJST = (hour, offset) => {
  if (hour === null || hour === '' || offset === null || offset === '') return '--:--';
  let jstHour = (parseFloat(hour) - parseFloat(offset) + 9) % 24;
  if (jstHour < 0) jstHour += 24;
  
  const h = Math.floor(jstHour);
  const m = (jstHour % 1) * 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

export default function App() {
  // ==========================================
  // 2. GLOBAL APP STATE & AUTH
  // ==========================================
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const [role, setRole] = useState(null); 
  const [activeTab, setActiveTab] = useState('clients'); 
  const [isDarkMode, setIsDarkMode] = useState(false); 
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const requestConfirm = (title, message, onConfirm) => setConfirmDialog({ isOpen: true, title, message, onConfirm });

  const [alertDialog, setAlertDialog] = useState({ isOpen: false, title: '', message: '' });
  const showAlert = (title, message) => setAlertDialog({ isOpen: true, title, message });
  
  const [brandSettings, setBrandSettings] = useState({
    logo: null,
    name: 'Sushiscribe Art Studio',
    accentColor: '#ff6147', 
    isSharp: true
  });
  
  const [clients, setClients] = useState(initialClients);
  const [activeClientId, setActiveClientId] = useState('client-1');
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  
  // Secure Link State
  const [linkModal, setLinkModal] = useState({ isOpen: false, targetType: null, stepId: null });
  const [linkData, setLinkData] = useState({ name: '', url: '' });

  // ==========================================
  // 3. UI INTERFACE STATE
  // ==========================================
  const [editingStepId, setEditingStepId] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [folderEditName, setFolderEditName] = useState('');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileFormData, setProfileFormData] = useState({});
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [activeFilters, setActiveFilters] = useState({}); 
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [activeCommentTexts, setActiveCommentTexts] = useState({}); 
  const [activeCommentFiles, setActiveCommentFiles] = useState({});
  const [reopenModalContext, setReopenModalContext] = useState(null); 
  const [reopenReasonText, setReopenReasonText] = useState('');
  const [newProjectModal, setNewProjectModal] = useState({ isOpen: false });
  const [newProjectName, setNewProjectName] = useState('');

  const [clientPasswordData, setClientPasswordData] = useState({ current: '', new: '', confirm: '' });

  // --- DEEP RESCUE FALLBACK (Fixes the White Screen of Death) ---
  const currentClient = clients[activeClientId] || { 
    profile: { name: 'Loading...', contact: '', plan: '', projectStatus: '' }, 
    folders: [], documents: [], projectHistory: [], pipeline: [] 
  };
  
  const isPipelineCompleted = currentClient.pipeline.length > 0 && currentClient.pipeline.every(step => step.status === 'completed');

  const getFolderPath = () => {
    const path = [];
    let curr = (currentClient.folders || []).find(f => f.id === currentFolderId);
    while (curr) {
      path.unshift(curr);
      curr = (currentClient.folders || []).find(f => f.id === curr.parentId);
    }
    return path;
  };
  const folderPath = getFolderPath();

  // ==========================================
  // 4. CORE LOGIC & DATA UPDATERS
  // ==========================================
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setRole(userData.role);
            
            if (userData.role === 'agency') {
              setActiveTab('clients');
            } else {
              setActiveClientId(userData.clientId);
              setActiveTab('pipeline');
            }
            setIsLoggedIn(true);
          } else {
            console.error("Missing User Document for UID:", user.uid);
            showAlert("Database Error", "Your account exists, but your user profile is missing from the database.");
            await signOut(auth);
          }
        } catch (error) {
          console.error("Firebase Rule Blocked Login:", error);
          showAlert("Permission Denied", "Security rules blocked access. Check your Firestore Database rules.");
          await signOut(auth);
        }
      } else {
        setIsLoggedIn(false);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !role) return;

    let unsubscribe;

    if (role === 'agency') {
      unsubscribe = onSnapshot(collection(db, "clients"), 
        (snapshot) => {
          const liveClients = {};
          snapshot.forEach((doc) => {
            const data = doc.data();
            liveClients[doc.id] = {
              profile: data.profile || { id: doc.id, name: 'Corrupted Client Data', contact: 'Unknown', plan: '', projectStatus: 'Unknown', customFields: [] },
              folders: data.folders || [],
              documents: data.documents || [],
              projectHistory: data.projectHistory || [],
              pipeline: data.pipeline || []
            };
          });
          setClients(liveClients);
        }, 
        (error) => console.error("Agency Listener Error:", error)
      );
    } else if (role === 'client' && activeClientId) {
      unsubscribe = onSnapshot(doc(db, "clients", activeClientId), 
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setClients(prev => ({
              ...prev,
              [activeClientId]: {
                profile: data.profile || { id: activeClientId, name: 'Client Data Loading', contact: '', plan: '', projectStatus: '', customFields: [] },
                folders: data.folders || [],
                documents: data.documents || [],
                projectHistory: data.projectHistory || [],
                pipeline: data.pipeline || []
              }
            }));
          }
        }, 
        (error) => console.error("Client Listener Error:", error)
      );
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isLoggedIn, role, activeClientId]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAlertDialog({ isOpen: false, title: '', message: '' }); 
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      setLoginPassword('');
    } catch (error) {
      console.error("FIREBASE LOGIN ERROR:", error.code, error.message);
      showAlert("Login Error", "Incorrect email or password. Please try again.");
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      showAlert("Error", "Please enter your email address.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      showAlert("Reset Link Sent", `If an account exists for ${resetEmail}, a secure password reset link has been sent. Please check your inbox.`);
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      console.error("FIREBASE ERROR:", error.code, error.message);
      showAlert("Error", "Failed to send reset link: " + error.message);
    }
  };

  const handleClientPasswordChange = async (e) => {
    e.preventDefault();
    
    if (!clientPasswordData.current) {
      showAlert("Missing Information", "Please enter your current temporary password.");
      return;
    }
    if (clientPasswordData.new !== clientPasswordData.confirm) {
      showAlert("Password Mismatch", "Your new passwords do not match. Please try again.");
      return;
    }
    if (clientPasswordData.new.length < 6) {
      showAlert("Weak Password", "Your new password must be at least 6 characters long.");
      return;
    }

    try {
      // 1. Verify their current temporary password is correct
      const credential = EmailAuthProvider.credential(auth.currentUser.email, clientPasswordData.current);
      await reauthenticateWithCredential(auth.currentUser, credential);

      // 2. Push the new secure password to Firebase Auth
      await updatePassword(auth.currentUser, clientPasswordData.new);
      
      // 3. Clear the lock flag in their database profile
      if (role === 'client' && currentClient.profile.requiresPasswordChange) {
         updateCurrentClient({ profile: { ...currentClient.profile, requiresPasswordChange: false } });
      }
      
      showAlert("Password Updated", "Your password has been successfully secured.");
      setClientPasswordData({ current: '', new: '', confirm: '' });
      
      // Unlock them and send them to their workspace!
      if (role === 'client') setActiveTab('pipeline');

    } catch (error) {
      console.error("Password Update Error:", error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        showAlert("Incorrect Password", "The current password you entered is incorrect.");
      } else {
        showAlert("Update Failed", "An error occurred while updating your password.");
      }
    }
  };

  const dismissPasswordWarning = () => {
    updateCurrentClient({ profile: { ...currentClient.profile, requiresPasswordChange: false } });
  };

  const handleLogout = async () => {
    try {
      await signOut(auth); 
      setIsLoggedIn(false);
      setLoginEmail('');
      setLoginPassword('');
      setRole(null);
      setActiveTab('clients');
      setIsMobileMenuOpen(false);
      setAlertDialog({ isOpen: false, title: '', message: '' }); 
    } catch (error) {
      console.error("Logout Error:", error);
    }
  };

  const updateBrand = (key, value) => {
    setBrandSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => updateBrand('logo', e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const updateCurrentClient = async (updates) => {
    if (!activeClientId) return;
    const existingClient = clients[activeClientId] || {
      profile: { id: activeClientId, name: 'New Client', contact: '', plan: '', projectStatus: 'New Onboarding', customFields: [] },
      folders: [], documents: [], projectHistory: [], pipeline: []
    };
    
    const updatedClientData = { ...existingClient, ...updates };
    
    setClients(prev => ({
      ...prev,
      [activeClientId]: updatedClientData
    }));
    
    try {
      await setDoc(doc(db, "clients", activeClientId), updatedClientData, { merge: true });
    } catch (error) {
      console.error("Firebase Sync Error:", error);
    }
  };

  const updateStep = (stepId, updatedStep) => {
    updateCurrentClient({
      pipeline: currentClient.pipeline.map(s => s.id === stepId ? updatedStep : s)
    });
  };

  const handleDeleteClient = (clientId) => {
    requestConfirm("Delete Client", "Are you sure you want to permanently delete this client and all associated data? This action cannot be undone.", async () => {
      await deleteDoc(doc(db, "clients", clientId));
      if (activeClientId === clientId) {
        setActiveClientId(null);
        setActiveTab('clients');
        setIsEditingProfile(false);
      }
    });
  };

  const handleDeleteDocument = (docId) => {
    requestConfirm("Delete Document", "Are you sure you want to permanently delete this document?", () => {
      updateCurrentClient({
        documents: currentClient.documents.filter(d => d.id !== docId)
      });
    });
  };

  const autoSyncFilesToDocuments = (projectName, phaseName, newFiles, subFolder = null) => {
    const client = clients[activeClientId];
    if (!client) return;

    let folders = [...(client.folders || [])];
    let documents = [...(client.documents || [])];
    const safeProjectName = projectName || 'Uncategorized Project';

    let projectFolder = folders.find(f => f.name === safeProjectName && !f.parentId);
    if (!projectFolder) {
      projectFolder = { id: `proj-${Date.now()}`, name: safeProjectName, parentId: null };
      folders.push(projectFolder);
    }

    let phaseFolder = folders.find(f => f.name === phaseName && f.parentId === projectFolder.id);
    if (!phaseFolder) {
      phaseFolder = { id: `phase-${Date.now()}`, name: phaseName, parentId: projectFolder.id };
      folders.push(phaseFolder);
    }

    let targetFolderId = phaseFolder.id;

    if (subFolder) {
      let subFolderObj = folders.find(f => f.name === subFolder && f.parentId === phaseFolder.id);
      if (!subFolderObj) {
        subFolderObj = { id: `sub-${Date.now()}`, name: subFolder, parentId: phaseFolder.id };
        folders.push(subFolderObj);
      }
      targetFolderId = subFolderObj.id;
    }

    const newDocs = newFiles.map(f => ({
      id: `doc-${Date.now()}-${Math.random()}`,
      name: f.name,
      size: f.size || "External Link",
      url: f.url,
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      folderId: targetFolderId
    }));

    updateCurrentClient({ 
      folders, 
      documents: [...newDocs, ...documents] 
    });
  };

  // --- SECURE EXTERNAL LINK ENGINE ---
  const handleOpenLinkModal = (targetType, stepId = null) => {
    setLinkModal({ isOpen: true, targetType, stepId });
    setLinkData({ name: '', url: '' });
  };

  const submitSecureLink = () => {
    if (!linkData.name.trim() || !linkData.url.trim()) {
      showAlert("Missing Info", "Please provide both a name and a valid URL.");
      return;
    }

    let safeUrl = linkData.url.trim();
    if (!safeUrl.startsWith('http://') && !safeUrl.startsWith('https://')) {
      safeUrl = 'https://' + safeUrl;
    }

    const newDoc = {
      id: `link-${Date.now()}`,
      name: linkData.name,
      size: "External Link",
      url: safeUrl,
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    };

    if (linkModal.targetType === 'workspace') {
      newDoc.folderId = currentFolderId;
      updateCurrentClient({ documents: [newDoc, ...(currentClient.documents || [])] });
    
    } else if (linkModal.targetType === 'pipelineEdit') {
      setEditFormData(prev => ({ 
        ...prev, 
        deliverables: [...(prev.deliverables || []), newDoc] 
      }));
      autoSyncFilesToDocuments(currentClient.profile.plan, editFormData.title, [newDoc]);
    
    } else if (linkModal.targetType === 'clientAction') {
      const step = currentClient.pipeline.find(s => s.id === linkModal.stepId);
      const newPipeline = currentClient.pipeline.map(s => {
        if (s.id === linkModal.stepId) return { ...s, status: 'completed', deliverables: [...(s.deliverables||[]), newDoc], actionRequired: null };
        if (s.id === linkModal.stepId + 1) return { ...s, status: 'current' };
        return s;
      });
      updateCurrentClient({ pipeline: newPipeline });
      autoSyncFilesToDocuments(currentClient.profile.plan, step.title, [newDoc], "Sent by the client");

    } else if (linkModal.targetType === 'comment') {
      setActiveCommentFiles(prev => ({
        ...prev,
        [linkModal.stepId]: [...(prev[linkModal.stepId] || []), newDoc]
      }));
    }

    setLinkModal({ isOpen: false, targetType: null, stepId: null });
  };

  // --- DOWNLOAD / OPEN HANDLER ---
  const handleDownloadFile = (url, fileName) => {
    if (!url) {
      showAlert("Error", "This file doesn't have a valid secure link associated with it.");
      return;
    }
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleProtectedStatusUpdate = (step, newStatus) => {
    const hasUnpaid = step.payments && step.payments.some(p => p.status === 'unpaid');
    if (hasUnpaid && (newStatus === 'completed' || newStatus === 'needs_action')) {
      showAlert("Unpaid Invoices Detected", "You cannot change this phase's status or request approval until all attached invoices are marked as PAID.");
      return;
    }
    handleUpdateStatus(step.id, newStatus);
  };

  const handleApprove = (stepId) => {
    const newPipeline = currentClient.pipeline.map(step => {
      if (step.id === stepId) return { ...step, approved: true, status: 'completed' };
      if (step.id === stepId + 1) return { ...step, status: step.requiresApproval ? 'needs_action' : 'current' };
      return step;
    });
    updateCurrentClient({ pipeline: newPipeline });
  };

  const handleUpdateStatus = (stepId, newStatus) => updateStep(stepId, { ...currentClient.pipeline.find(s => s.id === stepId), status: newStatus });
  
  const handleDeleteStep = (stepId) => {
    requestConfirm("Delete Phase", "Are you sure you want to securely delete this process phase?", () => {
      updateCurrentClient({ pipeline: currentClient.pipeline.filter(s => s.id !== stepId) });
      setEditingStepId(null);
    });
  };
  
  const saveEdit = () => {
    updateStep(editingStepId, editFormData);
    setEditingStepId(null);
  };

  const startEditing = (step) => {
    setEditingStepId(step.id);
    setEditFormData({ ...step, deliverables: step.deliverables || [], payments: step.payments || [] });
  };

  const handleAddStep = () => {
    if (!activeClientId || !clients[activeClientId]?.profile) {
      showAlert("No Client Selected", "You are trying to edit a project for a client that doesn't exist yet. Please select an active client from your directory first.");
      setActiveTab('clients'); 
      return;
    }

    const newStep = {
      id: Date.now() + Math.random(),
      title: "New Process Phase",
      description: "Description of the new phase.",
      status: "pending",
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      deadline: "",
      deliverables: [],
      requiresApproval: false,
      approved: false,
      payments: [],
      comments: [],
      auditLogs: []
    };
    
    updateCurrentClient({ pipeline: [...currentClient.pipeline, newStep] });
  };

  const handleAddPaymentGate = () => {
    setEditFormData(prev => ({
      ...prev,
      payments: [...(prev.payments || []), { id: `pay-${Date.now()}-${Math.random().toString(36).substring(2,9)}`, amount: '', status: 'unpaid', link: '' }]
    }));
  };

  const handleRemovePaymentGate = (paymentId) => {
    setEditFormData(prev => ({
      ...prev,
      payments: (prev.payments || []).filter(p => p.id !== paymentId)
    }));
  };

  const handlePaymentChange = (paymentId, field, value) => {
    setEditFormData(prev => ({
      ...prev,
      payments: (prev.payments || []).map(p => p.id === paymentId ? { ...p, [field]: value } : p)
    }));
  };

  const togglePaymentStatus = (stepId, paymentId) => {
    const newPipeline = currentClient.pipeline.map(step => {
      if (step.id === stepId && step.payments) {
        return { 
          ...step, 
          payments: step.payments.map(p => p.id === paymentId ? { ...p, status: p.status === 'paid' ? 'unpaid' : 'paid' } : p) 
        };
      }
      return step;
    });
    updateCurrentClient({ pipeline: newPipeline });
  };

  const triggerReopenModal = (step) => {
    setReopenModalContext(step);
    setReopenReasonText('');
  };

  const handleAddComment = (stepId) => {
    const commentText = activeCommentTexts[stepId];
    const commentFiles = activeCommentFiles[stepId] || [];
    
    if ((!commentText || !commentText.trim()) && commentFiles.length === 0) return;

    const targetStep = currentClient.pipeline.find(s => s.id === stepId);
    const authorName = role === 'client' ? `${currentClient.profile.contact} (Client)` : brandSettings.name;
    
    const timestamp = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });

    updateStep(stepId, {
      ...targetStep,
      comments: [...(targetStep.comments || []), { 
        id: Date.now(), 
        author: authorName, 
        text: commentText ? commentText.trim() : '', 
        timestamp,
        attachments: commentFiles
      }]
    });

    if (commentFiles.length > 0) {
      autoSyncFilesToDocuments(currentClient.profile.plan, targetStep.title, commentFiles, role === 'client' ? "Sent by the client" : null);
    }

    setActiveCommentTexts(prev => ({ ...prev, [stepId]: '' }));
    setActiveCommentFiles(prev => ({ ...prev, [stepId]: [] }));
  };

  const submitReopenPhase = () => {
    if (!reopenReasonText.trim() || !reopenModalContext) return;
    const timestamp = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });

    const newLog = { dateString: timestamp, reason: reopenReasonText.trim() };
    const updatedStep = {
      ...reopenModalContext,
      status: 'current',
      approved: false,
      auditLogs: [...(reopenModalContext.auditLogs || []), newLog]
    };

    updateStep(reopenModalContext.id, updatedStep);
    setReopenModalContext(null);
    setReopenReasonText('');
  };

  const handleCreateFolder = () => {
    const newFolder = { id: `folder-${Date.now()}-${Math.random().toString(36).substring(2,9)}`, name: 'New Folder', parentId: currentFolderId };
    updateCurrentClient({ folders: [...(currentClient.folders || []), newFolder] });
  };

  const handleSaveFolderName = (folderId) => {
    if (!folderEditName.trim()) return setEditingFolderId(null);
    const newFolders = (currentClient.folders || []).map(f => f.id === folderId ? { ...f, name: folderEditName } : f);
    updateCurrentClient({ folders: newFolders });
    setEditingFolderId(null);
  };

  const handleDeleteFolder = (folderId) => {
    updateCurrentClient({ 
      folders: (currentClient.folders || []).filter(f => f.id !== folderId && f.parentId !== folderId),
      documents: currentClient.documents.filter(d => d.folderId !== folderId)
    });
  };

  const removeFile = (index) => {
    const updated = [...(editFormData.deliverables || [])];
    updated.splice(index, 1);
    setEditFormData({ ...editFormData, deliverables: updated });
  };

  const handleAddNewClient = async () => {
    const newId = `client-${Date.now()}`;
    const newClient = {
      profile: { 
        id: newId, name: "New Client Company", contact: "", email: "", timezone: "", 
        contactStart: "", contactEnd: "", returningClient: false, plan: "", 
        joined: new Date().toISOString().split('T')[0], projectStatus: 'New Onboarding', customFields: [],
        requiresPasswordChange: true // <-- ADD THIS LINE
      },
      folders: [], documents: [], projectHistory: [], pipeline: []
    };
    
    await setDoc(doc(db, "clients", newId), newClient);
    
    setActiveClientId(newId);
    setIsClientDropdownOpen(false);
    setIsMobileMenuOpen(false);
    setActiveTab('profile');
    setProfileFormData(newClient.profile);
    setIsEditingProfile(true);
    setCurrentFolderId(null);
  };

  const startEditingProfile = () => {
    setProfileFormData(currentClient.profile);
    setIsEditingProfile(true);
  };

  const saveProfile = () => {
    updateCurrentClient({ profile: { ...profileFormData, id: activeClientId } });
    setIsEditingProfile(false);
  };

  const handleArchiveProject = () => {
    const newHistoryItem = {
      id: `hist-${Date.now()}-${Math.random().toString(36).substring(2,9)}`,
      projectName: currentClient.profile.plan || 'Custom Project',
      completedDate: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      pipeline: [...currentClient.pipeline]
    };
    updateCurrentClient({
      projectHistory: [newHistoryItem, ...(currentClient.projectHistory || [])],
      pipeline: [],
      profile: { ...currentClient.profile, projectStatus: 'Finished' }
    });
    setActiveTab('profile'); 
  };

  const handleStartNewProject = () => {
    if (currentClient.pipeline.length > 0 && !isPipelineCompleted) {
      requestConfirm(
        "Start New Project", 
        "The current pipeline is not fully completed. Starting a new project will clear the current workspace. Are you sure you want to proceed?", 
        () => setNewProjectModal({ isOpen: true })
      );
    } else {
      setNewProjectModal({ isOpen: true });
    }
  };

  const confirmStartNewProject = () => {
    if (!newProjectName.trim()) return;
    updateCurrentClient({
      pipeline: [], 
      profile: { 
        ...currentClient.profile, 
        plan: newProjectName,
        projectStatus: 'New Onboarding', 
        returningClient: currentClient.projectHistory?.length > 0 ? true : currentClient.profile.returningClient 
      }
    });
    setNewProjectModal({ isOpen: false });
    setNewProjectName('');
    setActiveTab('pipeline');
  };

  const handleAddCustomField = () => setProfileFormData(prev => ({ ...prev, customFields: [...(prev.customFields || []), { key: '', value: '' }] }));
  const handleCustomFieldChange = (index, field, value) => {
    const updatedFields = [...(profileFormData.customFields || [])];
    updatedFields[index][field] = value;
    setProfileFormData(prev => ({ ...prev, customFields: updatedFields }));
  };
  const handleRemoveCustomField = (index) => {
    const updatedFields = [...(profileFormData.customFields || [])];
    updatedFields.splice(index, 1);
    setProfileFormData(prev => ({ ...prev, customFields: updatedFields }));
  };

  const getAvailableFilters = () => {
    const filters = {};
    Object.values(clients).forEach(c => {
      if (c.profile.projectStatus) {
        if (!filters['Project Status']) filters['Project Status'] = new Set();
        filters['Project Status'].add(c.profile.projectStatus);
      }
      if (!filters['Client Type']) filters['Client Type'] = new Set();
      filters['Client Type'].add(c.profile.returningClient ? 'Returning Client' : 'New Client');
      if (c.profile.plan) {
        if (!filters['Plan']) filters['Plan'] = new Set();
        filters['Plan'].add(c.profile.plan);
      }
      (c.profile.customFields || []).forEach(cf => {
        if (cf.key && cf.value) {
          if (!filters[cf.key]) filters[cf.key] = new Set();
          filters[cf.key].add(cf.value);
        }
      });
    });
    Object.keys(filters).forEach(key => { filters[key] = Array.from(filters[key]).sort(); });
    return filters;
  };

  const toggleFilter = (category, value) => {
    setActiveFilters(prev => {
      const currentCatVals = prev[category] || [];
      const newCatVals = currentCatVals.includes(value) ? currentCatVals.filter(v => v !== value) : [...currentCatVals, value];
      const newFilters = { ...prev, [category]: newCatVals };
      if (newCatVals.length === 0) delete newFilters[category];
      return newFilters;
    });
  };

  const filteredClientsList = Object.values(clients).filter(c => {
    if (Object.keys(activeFilters).length === 0) return true;
    return Object.entries(activeFilters).every(([category, activeValues]) => {
      if (category === 'Project Status') return activeValues.includes(c.profile.projectStatus);
      if (category === 'Client Type') return activeValues.includes(c.profile.returningClient ? 'Returning Client' : 'New Client');
      if (category === 'Plan') return activeValues.includes(c.profile.plan);
      const customField = (c.profile.customFields || []).find(cf => cf.key === category);
      return customField && activeValues.includes(customField.value);
    });
  });

  const formatDeadline = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;

    const [year, month, day] = parts;
    const deadlineDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0,0,0,0);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    const formattedDate = deadlineDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    let daysText = '';
    if (diffDays > 1) daysText = `${diffDays} days left`;
    else if (diffDays === 1) daysText = '1 day left';
    else if (diffDays === 0) daysText = 'Due today';
    else if (diffDays === -1) daysText = '1 day overdue';
    else daysText = `${Math.abs(diffDays)} days overdue`;

    return `${formattedDate} (${daysText})`;
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '-';
    if (dateStr.includes(',')) return dateStr;
    const parts = dateStr.split('-');
    if(parts.length !== 3) return dateStr;
    return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': 
        return (
          <div className="w-8 h-8 rounded border-2 border-green-500 flex items-center justify-center bg-transparent">
            <Check className="w-5 h-5 text-green-500" strokeWidth={3} />
          </div>
        );
      case 'current': 
        return (
          <div className="w-8 h-8 rounded border-2 flex items-center justify-center shadow-sm bg-transparent" style={{ borderColor: brandSettings.accentColor }}>
            <Target className="w-4 h-4" style={{ color: brandSettings.accentColor }} />
          </div>
        );
      case 'needs_action': 
        return (
          <div className="w-8 h-8 rounded border-2 border-amber-500 flex items-center justify-center bg-transparent">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </div>
        );
      case 'pending': 
      default: 
        return (
          <div className="w-8 h-8 rounded border-2 border-gray-400 opacity-50 flex items-center justify-center bg-transparent">
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </div>
        );
    }
  };

  // ==========================================
  // 5. LOGIN SCREEN RENDER
  // ==========================================
  if (!isLoggedIn) {
    return (
      <div className={`min-h-screen flex items-center justify-center font-sans ${isDarkMode ? 'dark-mode' : ''}`} style={{ backgroundColor: isDarkMode ? '#071616' : '#d3d7c9' }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
          input[type="email"], input[type="password"] {
            background-color: ${isDarkMode ? '#071616' : '#d3d7c9'};
            color: ${isDarkMode ? '#eef2e7' : '#071616'};
            border: 1px solid ${isDarkMode ? '#334155' : 'rgba(0,0,0,0.1)'};
          }
        `}</style>
        
        <div className="w-full max-w-md p-8 shadow-xl relative" style={{ backgroundColor: isDarkMode ? '#273535' : '#eef2e7', borderRadius: brandSettings.isSharp ? '0px' : '1.5rem' }}>
          <button onClick={() => setIsDarkMode(!isDarkMode)} className="absolute top-4 right-4 p-2 rounded-lg transition-colors hover:opacity-70" title="Toggle Theme">
            {isDarkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-gray-700" />}
          </button>
          
          <div className="flex flex-col items-center mb-8">
            {brandSettings.logo ? (
              <img src={brandSettings.logo} alt="Logo" className="h-16 object-contain mb-4" />
            ) : (
              <div className="w-16 h-16 rounded-lg flex items-center justify-center text-white font-bold text-3xl shadow-md mb-4" style={{ backgroundColor: brandSettings.accentColor }}>
                {brandSettings.name.charAt(0)}
              </div>
              
            )}
            <h2 className="text-2xl font-bold text-center" style={{ color: isDarkMode ? '#eef2e7' : '#071616', fontFamily: "'Poppins', sans-serif" }}>
              {brandSettings.name}
            </h2>
            <p className="text-sm mt-1 opacity-70" style={{ color: isDarkMode ? '#eef2e7' : '#071616', fontFamily: "'Poppins', sans-serif" }}>
              {showForgotPassword ? 'Password Recovery' : 'Client Portal Access'}
            </p>
          </div>

          {/* --- ALERT BANNER --- */}
          {alertDialog.isOpen && (
            <div className="mb-6 p-4 rounded-xl border flex items-start gap-3 shadow-sm" style={{ backgroundColor: isDarkMode ? '#071616' : '#ffffff', borderColor: isDarkMode ? '#334155' : 'rgba(0,0,0,0.1)' }}>
              {alertDialog.title.includes('Error') || alertDialog.title.includes('Missing') || alertDialog.title.includes('Denied') ? (
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" />
              ) : (
                <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-green-500" />
              )}
              <div className="flex-1">
                <h4 className="text-sm font-bold" style={{ color: isDarkMode ? '#eef2e7' : '#071616' }}>{alertDialog.title}</h4>
                <p className="text-xs mt-1 opacity-70" style={{ color: isDarkMode ? '#eef2e7' : '#071616' }}>{alertDialog.message}</p>
              </div>
              <button type="button" onClick={() => setAlertDialog({isOpen: false, title: '', message: ''})} className="opacity-50 hover:opacity-100 transition-opacity" style={{ color: isDarkMode ? '#eef2e7' : '#071616' }}>
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {!showForgotPassword ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: isDarkMode ? '#eef2e7' : '#071616' }}>Email Address</label>
                <input 
                  type="email" 
                  required
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg focus:outline-none transition-all" 
                  placeholder="client@company.com"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="block text-sm font-medium" style={{ color: isDarkMode ? '#eef2e7' : '#071616' }}>Password</label>
                  <button type="button" onClick={() => setShowForgotPassword(true)} className="text-xs font-medium hover:underline opacity-80" style={{ color: brandSettings.accentColor }}>Forgot Password?</button>
                </div>
                <input 
                  type="password" 
                  required
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg focus:outline-none transition-all" 
                  placeholder="••••••••"
                />
              </div>
              
              <button 
                type="submit" 
                className="w-full flex items-center justify-center gap-2 py-3 mt-4 text-white font-bold rounded-lg transition-transform hover:opacity-90 active:scale-95 shadow-md"
                style={{ backgroundColor: brandSettings.accentColor, borderRadius: brandSettings.isSharp ? '0px' : '0.5rem' }}
              >
                <Lock className="w-4 h-4" /> Sign In
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div className="flex flex-col items-center justify-center mt-2 mb-6">
                <div 
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-4 shadow-sm" 
                  style={{ backgroundColor: `${brandSettings.accentColor}15`, color: brandSettings.accentColor }}
                >
                  <Lock className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold mb-2 tracking-tight" style={{ color: isDarkMode ? '#eef2e7' : '#071616' }}>
                  Reset Password
                </h3>
                <p className="text-sm opacity-70 text-center px-2" style={{ color: isDarkMode ? '#eef2e7' : '#071616', lineHeight: '1.6' }}>
                  Enter the email associated with your account and we will send a secure link to reset your password.
                </p>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium pl-1" style={{ color: isDarkMode ? '#eef2e7' : '#071616' }}>
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="w-4 h-4 opacity-50" style={{ color: isDarkMode ? '#eef2e7' : '#071616' }} />
                  </div>
                  <input 
                    type="email" 
                    required
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3.5 rounded-lg focus:outline-none transition-all" 
                    placeholder="client@company.com"
                  />
                </div>
              </div>

              <div className="pt-2">
                <button 
                  type="submit" 
                  className="w-full flex items-center justify-center gap-2 py-3.5 mt-2 text-white font-bold transition-all hover:opacity-90 active:scale-95 shadow-md"
                  style={{ 
                    backgroundColor: brandSettings.accentColor, 
                    borderRadius: brandSettings.isSharp ? '0px' : '0.5rem'
                  }}
                >
                  Send Reset Link
                </button>
              </div>

              <div className="flex justify-center mt-6 pt-2">
                <button 
                  type="button" 
                  onClick={() => setShowForgotPassword(false)} 
                  className="flex items-center gap-2 text-sm font-semibold transition-all hover:opacity-100 opacity-60 hover:-translate-x-1" 
                  style={{ color: isDarkMode ? '#eef2e7' : '#071616' }}
                >
                  <ArrowLeft className="w-4 h-4" /> Back to Login
                </button>
              </div>
            </form>
          )}
          
          <div className="mt-6 text-center text-xs opacity-50" style={{ color: isDarkMode ? '#eef2e7' : '#071616' }}>
            <p>Secure login required for access.</p>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // 6. MAIN APP RENDER
  // ==========================================
  return (
    <div className={`h-screen app-wrapper flex flex-col md:flex-row font-sans ${isDarkMode ? 'dark-mode' : ''}`}>
      
      {/* --- CSS THEME INJECTION --- */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        
        :root {
          --theme-bg: ${isDarkMode ? '#071616' : '#d3d7c9'};
          --theme-panel: ${isDarkMode ? '#273535' : '#eef2e7'};
          --theme-text: ${isDarkMode ? '#eef2e7' : '#071616'};
          --theme-border: transparent;
          --theme-input-border: ${isDarkMode ? '#334155' : 'rgba(0,0,0,0.1)'};
          --theme-subtle: ${isDarkMode ? 'rgba(238, 242, 231, 0.05)' : 'rgba(7, 22, 22, 0.05)'};
          --theme-accent: ${brandSettings.accentColor};
          
          --rad-sm: ${brandSettings.isSharp ? '0px' : '0.375rem'};
          --rad-md: ${brandSettings.isSharp ? '0px' : '0.5rem'};
          --rad-lg: ${brandSettings.isSharp ? '0px' : '0.75rem'};
          --rad-xl: ${brandSettings.isSharp ? '0px' : '1rem'};
          --rad-2xl: ${brandSettings.isSharp ? '0px' : '1.5rem'};
          --rad-full: ${brandSettings.isSharp ? '0px' : '9999px'};
        }

        .app-wrapper { background-color: var(--theme-bg) !important; color: var(--theme-text) !important; font-family: 'Poppins', sans-serif !important; font-weight: 300; }
        h1, h2, h3, h4, .font-bold { font-family: 'Boldonse', 'Poppins', sans-serif !important; font-weight: 700 !important; }
        .font-medium { font-weight: 500 !important; }
        .font-semibold { font-weight: 600 !important; }
        h1, h2, h3, h4, .text-gray-900, .text-gray-800 { color: var(--theme-text) !important; }
        p, span, .text-gray-700, .text-gray-600 { color: var(--theme-text) !important; opacity: 0.85; }
        .text-gray-500, .text-gray-400 { color: var(--theme-text) !important; opacity: 0.6; }
        
        .bg-white, .bg-slate-50, .bg-gray-50, .bg-gray-100 { 
           background-color: var(--theme-panel) !important; 
           border: none !important;
        }
        aside.bg-white { background-color: var(--theme-panel) !important; border-right: 2px solid var(--theme-bg) !important; }
        header.bg-white { background-color: var(--theme-panel) !important; border-bottom: 2px solid var(--theme-bg) !important; }

        input[type="text"], input[type="email"], input[type="date"], input[type="number"], input[type="password"], input[type="url"], textarea, select {
          background-color: var(--theme-bg) !important;
          color: var(--theme-text) !important;
          border: 1px solid var(--theme-input-border) !important;
        }

        .rounded { border-radius: var(--rad-sm) !important; }
        .rounded-md { border-radius: var(--rad-md) !important; }
        .rounded-lg { border-radius: var(--rad-lg) !important; }
        .rounded-xl { border-radius: var(--rad-xl) !important; }
        .rounded-2xl { border-radius: var(--rad-2xl) !important; }
        .rounded-full { border-radius: var(--rad-full) !important; }
      `}</style>

      {/* --- GLOBAL CONFIRMATION MODAL --- */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white w-full max-w-sm p-6 rounded-2xl shadow-2xl">
             <div className="flex items-center gap-3 mb-4">
               <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600">
                 <AlertTriangle className="w-5 h-5" />
               </div>
               <h3 className="text-lg font-bold text-gray-900">{confirmDialog.title}</h3>
             </div>
             <p className="text-sm text-gray-600 mb-8 leading-relaxed">{confirmDialog.message}</p>
             <div className="flex justify-end gap-3">
                <button onClick={() => setConfirmDialog({...confirmDialog, isOpen: false})} className="px-4 py-2 text-gray-700 hover:opacity-80 rounded-lg font-medium transition-colors" style={{ backgroundColor: 'var(--theme-bg)'}}>Cancel</button>
                <button onClick={() => { confirmDialog.onConfirm(); setConfirmDialog({...confirmDialog, isOpen: false}); }} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors">Confirm</button>
             </div>
          </div>
        </div>
      )}

      {/* --- GLOBAL ALERT MODAL --- */}
      {alertDialog.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white w-full max-w-sm p-6 rounded-2xl shadow-2xl flex flex-col items-center text-center">
            {alertDialog.title.includes('Error') || alertDialog.title.includes('Missing') || alertDialog.title.includes('Denied') ? (
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            ) : (
              <CheckCircle2 className="w-12 h-12 text-green-500 mb-4" />
            )}
            <h3 className="text-lg font-bold text-gray-900 mb-2">{alertDialog.title}</h3>
            <p className="text-sm text-gray-600 mb-6">{alertDialog.message}</p>
            <button onClick={() => setAlertDialog({isOpen: false, title: '', message: ''})} className="px-6 py-2 w-full text-white rounded-lg font-medium transition-opacity hover:opacity-90 shadow-sm" style={{ backgroundColor: brandSettings.accentColor }}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* --- SECURE LINK MODAL --- */}
      {linkModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white w-full max-w-sm p-6 rounded-2xl shadow-2xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Attach Secure Link</h3>
            <p className="text-xs text-gray-500 mb-4">Paste a secure Google Drive, Dropbox, or WeTransfer link here.</p>
            
            <div className="space-y-3 mb-6">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">File or Folder Name</label>
                <input type="text" value={linkData.name} onChange={e => setLinkData({...linkData, name: e.target.value})} placeholder="e.g., Final Brand Assets" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Secure URL</label>
                <input type="url" value={linkData.url} onChange={e => setLinkData({...linkData, url: e.target.value})} placeholder="https://drive.google.com/..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none" />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setLinkModal({ isOpen: false, targetType: null, stepId: null })} className="px-4 py-2 text-gray-700 hover:opacity-80 rounded-lg font-medium transition-colors" style={{ backgroundColor: 'var(--theme-bg)'}}>Cancel</button>
              <button onClick={submitSecureLink} className="px-4 py-2 text-white rounded-lg font-medium transition-colors shadow-sm" style={{ backgroundColor: brandSettings.accentColor }}>Attach Link</button>
            </div>
          </div>
        </div>
      )}

      {/* --- NEW PROJECT MODAL --- */}
      {newProjectModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white w-full max-w-sm p-6 rounded-2xl shadow-2xl">
             <h3 className="text-lg font-bold text-gray-900 mb-2">Name Your New Project</h3>
             <p className="text-sm text-gray-600 mb-4">What is the title or plan name for this new project?</p>
             <input 
               type="text" 
               value={newProjectName} 
               onChange={(e) => setNewProjectName(e.target.value)} 
               placeholder="e.g., Phase 2 Redesign" 
               className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-6 focus:outline-none"
             />
             <div className="flex justify-end gap-3">
                <button onClick={() => {setNewProjectModal({isOpen: false}); setNewProjectName('');}} className="px-4 py-2 text-gray-700 hover:opacity-80 rounded-lg font-medium transition-colors" style={{ backgroundColor: 'var(--theme-bg)'}}>Cancel</button>
                <button onClick={confirmStartNewProject} className="px-4 py-2 text-white rounded-lg font-medium transition-colors shadow-sm" style={{ backgroundColor: brandSettings.accentColor }}>Start Project</button>
             </div>
          </div>
        </div>
      )}

      {/* --- REOPEN PHASE MODAL --- */}
      {reopenModalContext && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white w-full max-w-sm p-6 rounded-2xl shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center text-rose-600">
                <RotateCcw className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Reopen Phase</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">Why are you reopening this phase? This will be added to the audit log.</p>
            <textarea
              value={reopenReasonText}
              onChange={(e) => setReopenReasonText(e.target.value)}
              placeholder="Client requested revisions..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-6 focus:outline-none resize-none"
              rows="3"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => { setReopenModalContext(null); setReopenReasonText(''); }} className="px-4 py-2 text-gray-700 hover:opacity-80 rounded-lg font-medium transition-colors" style={{ backgroundColor: 'var(--theme-bg)'}}>Cancel</button>
              <button onClick={submitReopenPhase} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-medium transition-colors shadow-sm">Confirm Reopen</button>
            </div>
          </div>
        </div>
      )}

      {/* --- NEW: MOBILE TOP HEADER --- */}
      <div className="md:hidden p-4 flex justify-between items-center shadow-sm z-30 sticky top-0" style={{ backgroundColor: 'var(--theme-panel)'}}>
        <div className="flex items-center gap-3">
          {brandSettings.logo ? (
            <img src={brandSettings.logo} alt="Logo" className="w-8 h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold shadow-md flex-shrink-0" style={{ backgroundColor: brandSettings.accentColor }}>
              {brandSettings.name.charAt(0)}
            </div>
          )}
          <div className="flex flex-col overflow-hidden">
            <span className="font-bold text-gray-800 tracking-tight truncate max-w-[150px] leading-tight">{brandSettings.name}</span>
            {role === 'client' && <span className="text-[10px] text-gray-500 uppercase tracking-widest font-semibold" style={{ color: brandSettings.accentColor }}>Client Portal</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 rounded-lg transition-colors" style={{ backgroundColor: 'var(--theme-bg)'}}>
            {isDarkMode ? <Sun className="w-4 h-4 text-yellow-400" /> : <Moon className="w-4 h-4 text-gray-700" />}
          </button>
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2 text-gray-600 hover:opacity-80 transition-colors">
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* --- NEW: MOBILE DARK OVERLAY --- */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* --- SIDEBAR NAVIGATION (UPDATED FOR MOBILE DRAWER) --- */}
      <aside className={`fixed inset-y-0 left-0 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 transition-transform duration-300 ease-in-out w-72 md:w-64 bg-white flex flex-col shadow-2xl md:shadow-sm z-50`} style={{ backgroundColor: 'var(--theme-panel)' }}>
        
        {/* Desktop Top Logo (Hidden on Mobile) */}
        <div className="hidden md:flex p-6 items-center space-x-3">
          {role === 'client' ? (
            <div className="flex flex-col overflow-hidden">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</span>
              <span className="font-bold text-gray-800 text-lg tracking-tight truncate">{currentClient.profile.name}</span>
            </div>
          ) : (
            <>
              {brandSettings.logo ? (
                <img src={brandSettings.logo} alt="Logo" className="w-8 h-8 object-contain" />
              ) : (
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xl shadow-md" style={{ backgroundColor: brandSettings.accentColor }}>
                  {brandSettings.name.charAt(0)}
                </div>
              )}
              <span className="font-bold text-gray-800 text-lg tracking-tight truncate">{brandSettings.name}</span>
            </>
          )}
        </div>
        
        {role === 'agency' && (
          <div className="px-4 pt-4 md:pt-0 relative">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-2">Active Client</p>
            <button onClick={() => setIsClientDropdownOpen(!isClientDropdownOpen)} className="w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors shadow-sm" style={{ backgroundColor: 'var(--theme-bg)'}}>
              <div className="flex items-center gap-2 truncate">
                <Users className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-800 truncate">{currentClient.profile.name}</span>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>

            {isClientDropdownOpen && (
              <div className="absolute top-full left-4 right-4 mt-2 shadow-2xl rounded-xl z-50 overflow-hidden border border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-bg)' }}>
                <div className="max-h-64 overflow-y-auto">
                  {Object.keys(clients).map(clientId => (
                    <button key={clientId} onClick={() => { 
                        setActiveClientId(clientId); 
                        setIsClientDropdownOpen(false); 
                        setIsEditingProfile(false);
                        setCurrentFolderId(null);
                        setIsMobileMenuOpen(false);
                      }} 
                      className={`w-full text-left px-4 py-3 text-sm transition-colors border-b border-[var(--theme-input-border)] last:border-0 ${clientId === activeClientId ? 'font-bold' : 'opacity-70 hover:opacity-100'}`} style={clientId === activeClientId ? { backgroundColor: `${brandSettings.accentColor}20`, color: brandSettings.accentColor } : { color: 'var(--theme-text)'}}>
                      {clients[clientId].profile.name}
                    </button>
                  ))}
                </div>
                <div className="p-3 border-t border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-panel)'}}>
                  <button onClick={handleAddNewClient} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors hover:opacity-90" style={{ color: brandSettings.accentColor, backgroundColor: `${brandSettings.accentColor}15` }}>
                    <Plus className="w-4 h-4" /> Add New Client
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        
        <nav className="flex-1 p-4 space-y-2 mt-2 overflow-y-auto">
          {activeClientId && (
            <>
              <button onClick={() => { setActiveTab('pipeline'); setIsMobileMenuOpen(false); }} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'pipeline' ? 'font-bold shadow-sm' : ''}`} style={activeTab === 'pipeline' ? { backgroundColor: brandSettings.accentColor, color: '#ffffff' } : { backgroundColor: 'transparent' }}>
                <LayoutDashboard className="w-5 h-5" /> <span>Active Project</span>
              </button>
              {role === 'agency' && (
                <button onClick={() => { setActiveTab('documents'); setIsMobileMenuOpen(false); }} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'documents' ? 'font-bold shadow-sm' : ''}`} style={activeTab === 'documents' ? { backgroundColor: brandSettings.accentColor, color: '#ffffff' } : { backgroundColor: 'transparent' }}>
                  <Folder className="w-5 h-5" /> <span>Client Documents</span>
                </button>
              )}
              <button onClick={() => { setActiveTab('profile'); setIsMobileMenuOpen(false); }} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'profile' ? 'font-bold shadow-sm' : ''}`} style={activeTab === 'profile' ? { backgroundColor: brandSettings.accentColor, color: '#ffffff' } : { backgroundColor: 'transparent' }}>
                <User className="w-5 h-5" /> <span>Client Profile</span>
              </button>
            </>
          )}
          
          {role === 'agency' && (
            <div className="pt-4 mt-4 space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-4">Agency Tools</p>
              <button onClick={() => { setActiveTab('clients'); setIsMobileMenuOpen(false); }} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'clients' ? 'font-bold shadow-sm' : ''}`} style={activeTab === 'clients' ? { backgroundColor: brandSettings.accentColor, color: '#ffffff' } : { backgroundColor: 'transparent' }}>
                <Users className="w-5 h-5" /> <span>Clients Directory</span>
              </button>
              <button onClick={() => { setActiveTab('settings'); setIsMobileMenuOpen(false); }} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'settings' ? 'font-bold shadow-sm' : ''}`} style={activeTab === 'settings' ? { backgroundColor: brandSettings.accentColor, color: '#ffffff' } : { backgroundColor: 'transparent' }}>
                <Settings className="w-5 h-5" /> <span>Portal Settings</span>
              </button>
            </div>
          )}

          {role === 'client' && (
            <div className="pt-4 mt-4 space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-4">Account</p>
              <button onClick={() => { setActiveTab('settings'); setIsMobileMenuOpen(false); }} className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${activeTab === 'settings' ? 'font-bold shadow-sm' : ''}`} style={activeTab === 'settings' ? { backgroundColor: brandSettings.accentColor, color: '#ffffff' } : { backgroundColor: 'transparent' }}>
                <Settings className="w-5 h-5" /> <span>Security & Settings</span>
              </button>
            </div>
          )}
        </nav>

        <div className="p-4 mt-auto">
          <button 
            onClick={handleLogout} 
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold rounded-xl transition-colors hover:opacity-80"
            style={{ backgroundColor: isDarkMode ? 'rgba(225, 29, 72, 0.1)' : '#ffe4e6', color: isDarkMode ? '#fb7185' : '#be123c' }}
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </aside>

      {/* --- MAIN CONTENT WINDOW --- */}
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
        
        {/* Desktop Top Header (Hidden on Mobile) */}
        <header className="bg-white px-8 py-5 justify-between items-center hidden md:flex z-0 shadow-sm border-b border-[var(--theme-bg)]">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {activeTab === 'pipeline' && 'Active Project'}
              {activeTab === 'documents' && 'Client Documents'}
              {activeTab === 'profile' && 'Client Profile'}
              {activeTab === 'clients' && 'Clients Directory'}
              {activeTab === 'settings' && role === 'agency' && 'Portal Settings'}
              {activeTab === 'settings' && role === 'client' && 'Security & Settings'}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {role === 'client' ? `Hi, ${currentClient.profile.contact || currentClient.profile.name}, welcome back!` : (activeClientId ? `Managing: ${currentClient.profile.name}` : 'Agency Dashboard')}
            </p>
          </div>
          <div className="flex items-center space-x-6">
             <div className="hidden lg:block text-right border-r border-[var(--theme-input-border)] pr-6 mr-2">
               <h3 className="font-bold text-gray-800 leading-tight tracking-tight">{brandSettings.name}</h3>
               <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: brandSettings.accentColor }}>Client Portal</p>
             </div>
             <button onClick={() => setIsDarkMode(!isDarkMode)} className="p-2 rounded-lg transition-colors" style={{ backgroundColor: 'var(--theme-bg)'}} title="Toggle Theme">
               {isDarkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-gray-700" />}
             </button>
             <div className="flex items-center space-x-4">
               <div className="text-right mr-2">
                 <p className="text-sm font-medium text-gray-900">{role === 'client' ? currentClient.profile.contact || currentClient.profile.name : 'Agency Admin'}</p>
                 <p className="text-xs text-gray-500">{role === 'client' ? 'Client' : 'Manager'}</p>
               </div>
               <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: brandSettings.accentColor }}>
                 {role === 'client' ? (currentClient.profile.contact ? currentClient.profile.contact.charAt(0) : 'C') : 'AA'}
               </div>
             </div>
          </div>
        </header>

        {/* Scrollable View Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-4xl mx-auto">

            {/* --- SOFT WARNING BANNER --- */}
            {role === 'client' && currentClient.profile?.requiresPasswordChange && (
              <div className="mb-6 p-4 rounded-xl border border-amber-200 shadow-sm flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between transition-all" style={{ backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.1)' : '#fffbeb' }}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold" style={{ color: isDarkMode ? '#fbbf24' : '#92400e' }}>Update Your Password</h4>
                    <p className="text-xs mt-1" style={{ color: isDarkMode ? 'rgba(251, 191, 36, 0.8)' : '#b45309' }}>For your security, we recommend changing your temporary password in the Security & Settings tab.</p>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs font-bold cursor-pointer transition-opacity hover:opacity-80" style={{ color: isDarkMode ? '#fbbf24' : '#b45309' }}>
                  <input type="checkbox" onChange={dismissPasswordWarning} className="rounded text-amber-600 focus:ring-amber-500 cursor-pointer w-4 h-4" />
                  Don't show again
                </label>
              </div>
            )}
            
            {/* ========================================== */}
            {/* VIEW: PROCESS PIPELINE */}
            {/* ========================================== */}
            {activeTab === 'pipeline' && activeClientId && (
              <div className="space-y-8">
                
                <div className="bg-white rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4 shadow-sm border border-[var(--theme-input-border)]">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center bg-opacity-10" style={{ backgroundColor: `${brandSettings.accentColor}15`, color: brandSettings.accentColor }}>
                      <Briefcase className="w-8 h-8" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">{currentClient.profile.plan || 'Custom Project'}</h2>
                      <p className="text-gray-500 text-sm">Status: <span className="font-medium text-gray-700">{currentClient.profile.projectStatus}</span></p>
                    </div>
                  </div>
                  <div className="flex gap-8">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-gray-800">{currentClient.pipeline.filter(s => s.status === 'completed').length}/{currentClient.pipeline.length}</p>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Steps Done</p>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold" style={{ color: brandSettings.accentColor }}>
                        {currentClient.pipeline.length > 0 ? Math.round((currentClient.pipeline.filter(s => s.status === 'completed').length / currentClient.pipeline.length) * 100) : 0}%
                      </p>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Progress</p>
                    </div>
                  </div>
                </div>

                <div className="relative pl-4 md:pl-0">
                  <div className="absolute left-8 md:left-8 top-8 bottom-8 w-1 rounded-full hidden md:block" style={{ backgroundColor: 'var(--theme-bg)'}}></div>
                  
                  <div className="space-y-6">
                    {currentClient.pipeline.map((step, index) => {
                      const isLocked = step.status === 'completed';
                      return (
                        <div key={step.id} className="relative flex flex-col md:flex-row items-start gap-4 md:gap-6 group">
                          
                          <div className="hidden md:flex flex-col items-center z-10 pt-4">
                            {getStatusIcon(step.status)}
                            {index !== currentClient.pipeline.length - 1 && (
                              <div className={`w-0.5 h-full my-2 transition-colors duration-500 ${step.status === 'completed' ? 'bg-green-400' : ''}`} style={step.status !== 'completed' ? { backgroundColor: 'var(--theme-bg)' } : {}}></div>
                            )}
                          </div>

                          <div className="flex-1 bg-white rounded-2xl p-6 shadow-sm transition-all duration-300 w-full overflow-hidden border border-[var(--theme-input-border)]">
                            
                            {editingStepId === step.id ? (
                              <div className="space-y-4">
                                <div className="flex justify-between items-center mb-2">
                                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-500">Editing Step {index + 1}</h3>
                                  <button onClick={() => setEditingStepId(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-500 mb-1 flex justify-between items-center">
                                    Step Title
                                  </label>
                                  <input type="text" value={editFormData.title} onChange={e => setEditFormData({...editFormData, title: e.target.value})} className="w-full px-3 py-2 rounded-lg focus:outline-none" />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-500 mb-1">Description</label>
                                  <textarea value={editFormData.description} onChange={e => setEditFormData({...editFormData, description: e.target.value})} className="w-full px-3 py-2 rounded-lg focus:outline-none" rows="3" />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-gray-500 mb-1">Target Deadline</label>
                                  <div className="relative">
                                    <Calendar className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                                    <input type="date" value={editFormData.deadline || ''} onChange={e => setEditFormData({...editFormData, deadline: e.target.value})} className="w-full pl-9 pr-3 py-2 rounded-lg focus:outline-none text-sm" />
                                  </div>
                                </div>
                                <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--theme-bg)'}}>
                                  <label className="block text-xs font-semibold text-gray-500 mb-2">Attached Files / Deliverables</label>
                                  <div className="space-y-2 mb-3">
                                    {(editFormData.deliverables || []).map((file, i) => (
                                      <div key={i} className="flex items-center justify-between bg-white p-2 rounded-lg text-sm border border-[var(--theme-input-border)]">
                                        <div className="flex items-center gap-2 overflow-hidden"><FileText className="w-4 h-4 text-blue-500 flex-shrink-0" /><span className="truncate">{file.name}</span></div>
                                        <button onClick={() => removeFile(i)} className="text-gray-400 hover:text-red-500 p-1"><X className="w-4 h-4"/></button>
                                      </div>
                                    ))}
                                  </div>
                                  <div>
                                      <button type="button" onClick={() => handleOpenLinkModal('pipelineEdit')} className="flex items-center justify-center gap-2 px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-700 transition-colors cursor-pointer w-max shadow-sm border border-[var(--theme-input-border)]">
                                        <UploadCloud className="w-4 h-4" /> Attach Secure Link
                                      </button>
                                  </div>
                                </div>
                                <div className="p-4 rounded-xl mt-4" style={{ backgroundColor: 'var(--theme-bg)'}}>
                                  <div className="flex justify-between items-center mb-3">
                                    <label className="block text-xs font-semibold text-gray-500">Payment Gates</label>
                                    <button onClick={handleAddPaymentGate} className="flex items-center gap-1 text-xs font-medium transition-colors" style={{ color: brandSettings.accentColor }}>
                                      <Plus className="w-3 h-3" /> Add Invoice
                                    </button>
                                  </div>
                                  
                                  {(!editFormData.payments || editFormData.payments.length === 0) && (
                                    <p className="text-xs text-gray-400 italic">No payment gates required for this phase.</p>
                                  )}

                                  <div className="space-y-3">
                                    {(editFormData.payments || []).map((pay, pIdx) => (
                                      <div key={pay.id} className="flex flex-col sm:flex-row gap-3 pt-3 mt-3 first:mt-0 first:border-0 first:pt-0" style={{ borderTop: pIdx !== 0 ? '1px solid var(--theme-input-border)' : 'none' }}>
                                        <div className="flex-[1]">
                                          <label className="block text-[10px] font-medium text-gray-500 mb-1">Invoice Amount ($)</label>
                                          <div className="relative"><DollarSign className="w-3 h-3 absolute left-2.5 top-2.5 text-gray-400" /><input type="number" value={pay.amount || ''} onChange={(e) => handlePaymentChange(pay.id, 'amount', e.target.value)} className="w-full pl-7 pr-2 py-1.5 rounded focus:outline-none text-sm" placeholder="0.00" /></div>
                                        </div>
                                        <div className="flex-[2]">
                                          <label className="block text-[10px] font-medium text-gray-500 mb-1">Payment Link</label>
                                          <div className="flex gap-2">
                                            <input type="text" value={pay.link || ''} onChange={(e) => handlePaymentChange(pay.id, 'link', e.target.value)} className="w-full px-2 py-1.5 rounded focus:outline-none text-sm" placeholder="https://..." />
                                            <button onClick={() => handleRemovePaymentGate(pay.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded bg-white transition-colors border border-[var(--theme-input-border)]"><Trash2 className="w-4 h-4" /></button>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className="pt-4 mt-4 flex flex-col sm:flex-row justify-between items-center gap-3">
                                  <button onClick={() => handleDeleteStep(step.id)} className="w-full sm:w-auto flex items-center justify-center gap-1 px-3 py-2 text-sm text-red-600 hover:opacity-80 rounded-lg"><Trash2 className="w-4 h-4" /> Delete Phase</button>
                                  <div className="flex w-full sm:w-auto gap-2">
                                    <button onClick={() => setEditingStepId(null)} className="flex-1 sm:flex-none px-4 py-2 text-sm text-gray-600 rounded-lg" style={{ backgroundColor: 'var(--theme-bg)'}}>Cancel</button>
                                    <button onClick={saveEdit} className="flex-1 sm:flex-none px-4 py-2 text-sm text-white rounded-lg shadow-sm" style={{ backgroundColor: brandSettings.accentColor }}>Save</button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="flex justify-between items-start mb-4">
                                  <div>
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                      <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Step {index + 1}</span>
                                      {step.status === 'completed' && <span className="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap" style={{ backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : '#dcfce7', color: isDarkMode ? '#4ade80' : '#166534' }}>Completed & Locked</span>}
                                      {step.status === 'current' && <span className="px-2 py-0.5 rounded-full text-white text-xs font-medium whitespace-nowrap" style={{ backgroundColor: brandSettings.accentColor }}>In Progress</span>}
                                      {step.status === 'needs_action' && <span className="px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 whitespace-nowrap" style={{ backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7', color: isDarkMode ? '#fbbf24' : '#b45309' }}><AlertCircle className="w-3 h-3"/> Action Required</span>}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-800">{step.title}</h3>
                                  </div>
                                </div>
                                
                                {step.deadline && step.status !== 'completed' && (
                                  <div className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium px-2.5 py-1.5 rounded-lg text-amber-700" style={{ backgroundColor: 'rgba(245, 158, 11, 0.1)'}}>
                                    <Calendar className="w-4 h-4" /> Deadline: {formatDeadline(step.deadline)}
                                  </div>
                                )}
                                
                                {step.deadline && step.status === 'completed' && (
                                  <div className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded text-gray-500" style={{ backgroundColor: 'var(--theme-bg)'}}>
                                    <Calendar className="w-3 h-3" /> Original Deadline: {formatDisplayDate(step.deadline)}
                                  </div>
                                )}

                                <p className="text-gray-600 mb-4 text-sm md:text-base">{step.description}</p>

                                {step.auditLogs && step.auditLogs.map((log, lIdx) => (
                                  <p key={lIdx} className="text-xs text-rose-600 font-medium italic mt-1 p-2 rounded" style={{ backgroundColor: 'rgba(225, 29, 72, 0.1)'}}>
                                    * This phase was reopened on {log.dateString} for: "{log.reason}"
                                  </p>
                                ))}
                                
                                {step.deliverables && step.deliverables.length > 0 && (
                                  <div className="my-6">
                                    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Attached Files</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                      {step.deliverables.map((file, i) => (
                                        <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg" style={{ backgroundColor: 'var(--theme-bg)'}}>
                                          <div className="flex items-center gap-2 overflow-hidden"><FileText className="w-4 h-4 text-gray-400 flex-shrink-0" /><span className="text-sm text-gray-700 truncate">{file.name}</span></div>
                                          <button onClick={(e) => { e.stopPropagation(); handleDownloadFile(file.url, file.name); }} className="p-2 text-gray-400 hover:text-blue-600 rounded-lg transition-colors flex-shrink-0"><ExternalLink className="w-4 h-4" /></button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {role === 'client' && !isLocked && step.status === 'needs_action' && step.actionRequired === 'Upload files' && (
                                  <div className="rounded-xl p-4 mb-4 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.1)' : '#fef3c7' }}>
                                    <div><h4 className="font-medium" style={{ color: isDarkMode ? '#fbbf24' : '#92400e' }}>Upload Requested Files</h4></div>
                                    <div className="w-full sm:w-auto">
                                      <button onClick={() => handleOpenLinkModal('clientAction', step.id)} className="flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg font-medium text-sm whitespace-nowrap shadow-sm w-full">
                                        <UploadCloud className="w-4 h-4" /> Provide Link
                                      </button>
                                    </div>
                                  </div>
                                )}

                                {role === 'client' && !isLocked && step.status === 'needs_action' && step.requiresApproval && !step.approved && (
                                  <div className="rounded-xl p-4 mb-4 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ backgroundColor: `${brandSettings.accentColor}10` }}>
                                    <div><h4 className="font-medium" style={{ color: brandSettings.accentColor }}>Approval Required</h4></div>
                                    <div className="flex gap-2 w-full sm:w-auto">
                                      <button onClick={() => handleApprove(step.id)} className="w-full px-4 py-2 text-white rounded-lg font-medium text-sm shadow-sm" style={{ backgroundColor: brandSettings.accentColor }}>Approve Step</button>
                                    </div>
                                  </div>
                                )}

                                {(step.payments || []).map((pay, pIdx) => (
                                  <div key={pay.id} className="mt-4 rounded-xl p-4" style={{ backgroundColor: pay.status === 'paid' ? (isDarkMode ? 'rgba(34, 197, 94, 0.1)' : '#dcfce7') : (isDarkMode ? 'rgba(225, 29, 72, 0.1)' : '#ffe4e6') }}>
                                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                      <div>
                                        <div className="flex items-center gap-2 mb-1"><CreditCard className={`w-5 h-5 ${pay.status === 'paid' ? (isDarkMode ? 'text-green-400' : 'text-green-600') : (isDarkMode ? 'text-rose-400' : 'text-rose-600')}`} /><h4 className="font-bold text-gray-900">Phase Invoice {step.payments?.length > 1 ? `#${pIdx + 1}` : ''}: ${pay.amount}</h4></div>
                                        <p className="text-sm text-gray-600">Status: <strong className="uppercase tracking-wider text-xs">{pay.status}</strong></p>
                                      </div>
                                      <div className="flex items-center gap-2 w-full sm:w-auto">
                                        {role === 'client' && pay.link && (
                                          <a href={pay.link} target="_blank" rel="noreferrer" className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg text-sm shadow-sm transition-colors ${pay.status === 'paid' ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}`}><ExternalLink className="w-4 h-4" /> {pay.status === 'paid' ? 'View Receipt' : 'Pay Now'}</a>
                                        )}
                                        {role === 'agency' && !isLocked && (
                                          <button onClick={() => togglePaymentStatus(step.id, pay.id)} className={`flex-1 sm:flex-none px-4 py-2 rounded-lg text-sm font-medium shadow-sm ${pay.status === 'paid' ? 'bg-white text-gray-700' : 'bg-green-500 text-white'}`}>
                                            {pay.status === 'paid' ? 'Mark Unpaid' : 'Mark Paid'}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}

                                <div className="mt-6 p-3 sm:p-4 rounded-xl" style={{ backgroundColor: 'var(--theme-bg)'}}>
                                  <div className="flex justify-between items-center mb-3">
                                    <h4 className="text-xs font-bold uppercase text-gray-500 tracking-wider flex items-center gap-1.5"><MessageSquare className="w-4 h-4 text-gray-500" /> Phase Feedback Loop</h4>
                                  </div>

                                  <div className="space-y-2 mb-4 max-h-48 overflow-y-auto pr-1">
                                    {step.comments && step.comments.map(comment => {
                                      const isAgency = !comment.author.includes('(Client)');
                                      return (
                                        <div key={comment.id} className="bg-white p-2.5 rounded-lg text-sm shadow-sm border border-[var(--theme-input-border)]">
                                          <div className="flex justify-between items-center mb-1 text-xs text-gray-500 font-medium"><span>{isAgency ? brandSettings.name : comment.author}</span><span className="opacity-75">{comment.timestamp}</span></div>
                                          {comment.text && <p className={`text-gray-700 leading-relaxed whitespace-pre-line ${isAgency ? 'font-medium' : 'font-normal'}`}>{comment.text}</p>}
                                          {comment.attachments && comment.attachments.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                              {comment.attachments.map((f, i) => (
                                                <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded text-xs text-gray-600 border border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-bg)' }}>
                                                  <FileText className="w-3 h-3 text-gray-400" /> <span className="truncate max-w-[100px] sm:max-w-[150px]">{f.name}</span>
                                                  <button onClick={(e) => { e.stopPropagation(); handleDownloadFile(f.url, f.name); }} className="hover:text-blue-500 ml-1"><ExternalLink className="w-3 h-3"/></button>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {!isLocked ? (
                                    (() => {
                                      const hasDraft = (activeCommentTexts[step.id] && activeCommentTexts[step.id].trim().length > 0) || (activeCommentFiles[step.id] && activeCommentFiles[step.id].length > 0);
                                      return (
                                        <div className={`flex flex-col gap-2 p-3 sm:-mx-3 rounded-xl transition-all duration-300 ${hasDraft ? 'border-2 border-amber-400 shadow-md' : 'border-2 border-transparent'}`} style={{ backgroundColor: hasDraft ? (isDarkMode ? 'rgba(245, 158, 11, 0.1)' : '#fffbeb') : 'transparent' }}>
                                          {hasDraft && (
                                            <div className="flex items-center gap-1.5 text-amber-600 mb-1 px-1">
                                              <AlertCircle className="w-4 h-4 animate-bounce" />
                                              <span className="text-xs font-bold uppercase tracking-wider">Unsent Draft</span>
                                            </div>
                                          )}
                                          {(activeCommentFiles[step.id] || []).length > 0 && (
                                            <div className="flex flex-wrap gap-2 mb-1 px-1">
                                              {activeCommentFiles[step.id].map((file, i) => (
                                                <div key={i} className="flex items-center gap-1 text-xs bg-white px-2 py-1 rounded shadow-sm border border-[var(--theme-input-border)]">
                                                  <FileText className="w-3 h-3 text-blue-500" />
                                                  <span className="truncate max-w-[120px]">{file.name}</span>
                                                  <button onClick={() => {
                                                    const newFiles = [...activeCommentFiles[step.id]];
                                                    newFiles.splice(i, 1);
                                                    setActiveCommentFiles({...activeCommentFiles, [step.id]: newFiles});
                                                  }} className="text-gray-400 hover:text-red-500"><X className="w-3 h-3"/></button>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          <div className="flex gap-2">
                                            {role === 'client' && (
                                              <button type="button" onClick={() => handleOpenLinkModal('comment', step.id)} className="p-2 bg-white rounded-lg shadow-sm cursor-pointer hover:opacity-80 flex items-center justify-center border border-[var(--theme-input-border)] transition-colors">
                                                <Paperclip className="w-4 h-4 text-gray-500" />
                                              </button>
                                            )}
                                            <div className="flex-1 relative flex items-center">
                                              <input type="text" placeholder="Add information..." value={activeCommentTexts[step.id] || ''} onChange={e => setActiveCommentTexts({...activeCommentTexts, [step.id]: e.target.value})} onKeyDown={e => e.key === 'Enter' && handleAddComment(step.id)} className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none shadow-sm border border-[var(--theme-input-border)]" />
                                            </div>
                                            <button onClick={() => handleAddComment(step.id)} className="p-2 text-white rounded-lg shadow-sm hover:opacity-90 transition-transform active:scale-95" style={{ backgroundColor: brandSettings.accentColor }}><Send className="w-4 h-4" /></button>
                                          </div>
                                        </div>
                                      );
                                    })()
                                  ) : (
                                    <p className="text-xs text-center text-gray-500 py-1.5 font-medium">Feedback locked. Reopen phase to post updates.</p>
                                  )}
                                </div>

                                {role === 'agency' && (
                                  <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
                                     <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                                       {!isLocked ? (
                                         <>
                                           <button onClick={() => handleProtectedStatusUpdate(step, 'completed')} className="text-xs px-3 py-2 bg-white text-gray-700 rounded font-semibold shadow-sm w-full sm:w-auto border border-[var(--theme-input-border)] hover:bg-gray-50 transition-colors">Mark Completed & Lock</button>
                                           {step.requiresApproval && step.status !== 'needs_action' && <button onClick={() => handleProtectedStatusUpdate(step, 'needs_action')} className="text-xs px-3 py-2 bg-amber-50 text-amber-700 rounded font-medium w-full sm:w-auto hover:bg-amber-100 transition-colors">Request Client Approval</button>}
                                           {step.status === 'pending' && <button onClick={() => handleUpdateStatus(step.id, 'current')} className="text-xs px-3 py-2 text-white rounded font-medium shadow-sm w-full sm:w-auto hover:opacity-90 transition-opacity" style={{ backgroundColor: brandSettings.accentColor }}>Start Step</button>}
                                         </>
                                       ) : (
                                         <button onClick={() => triggerReopenModal(step)} className="text-xs px-3 py-2 text-rose-700 rounded font-semibold flex items-center justify-center gap-1 w-full sm:w-auto hover:opacity-80 transition-opacity" style={{ backgroundColor: 'rgba(225, 29, 72, 0.1)'}}><RotateCcw className="w-3 h-3" /> Reopen the Phase</button>
                                       )}
                                     </div>
                                     {!isLocked && <button onClick={() => startEditing(step)} className="flex items-center justify-center gap-1 text-xs px-3 py-2 text-gray-500 hover:opacity-80 rounded w-full sm:w-auto border border-gray-200 sm:border-0"><Edit3 className="w-3 h-3" /> Edit Step</button>}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {role === 'agency' && (
                    <div className="pt-6 pl-0 md:pl-12 flex flex-col gap-3">
                      {!isPipelineCompleted && <button onClick={handleAddStep} className="flex items-center gap-2 px-4 py-3 bg-white rounded-xl text-gray-500 font-medium w-full justify-center shadow-sm border border-[var(--theme-input-border)] hover:bg-gray-50 transition-colors"><Plus className="w-5 h-5" /> Add New Phase</button>}
                      {isPipelineCompleted && <button onClick={handleArchiveProject} className="flex items-center gap-2 px-4 py-3 text-white rounded-xl font-medium w-full justify-center shadow-md hover:opacity-90 transition-opacity" style={{ backgroundColor: brandSettings.accentColor }}><Archive className="w-5 h-5" /> All Steps Completed — Send to Project History</button>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ========================================== */}
            {/* VIEW: CLIENT DOCUMENTS */}
            {/* ========================================== */}
            {activeTab === 'documents' && activeClientId && role === 'agency' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Saved Documents</h2>
                    <p className="text-gray-500 text-sm mt-1">Manage files and folders for {currentClient.profile.name}.</p>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <button onClick={handleCreateFolder} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-700 shadow-sm border border-[var(--theme-input-border)]"><FolderPlus className="w-4 h-4" /> New Folder</button>
                    <button onClick={() => handleOpenLinkModal('workspace')} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 text-white rounded-lg text-sm font-medium shadow-sm" style={{ backgroundColor: brandSettings.accentColor }}>
                      <UploadCloud className="w-4 h-4" /> Attach Secure Link
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm p-3 sm:p-6 border border-[var(--theme-input-border)]">
                  <div className="flex items-center gap-2 mb-6 px-1 sm:px-0 text-sm font-medium text-gray-500 overflow-x-auto whitespace-nowrap pb-2 scrollbar-hide">
                    <button onClick={() => setCurrentFolderId(null)} className={`hover:text-gray-900 transition-colors ${!currentFolderId ? 'text-gray-900 font-bold' : ''}`}>Home</button>
                    {folderPath.map((f, i) => (
                      <React.Fragment key={f.id}>
                        <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                        <button onClick={() => setCurrentFolderId(f.id)} className={`hover:text-gray-900 transition-colors ${i === folderPath.length - 1 ? 'text-gray-900 font-bold' : ''}`}>{f.name}</button>
                      </React.Fragment>
                    ))}
                    {currentFolderId && <button onClick={() => setCurrentFolderId(folderPath.length > 1 ? folderPath[folderPath.length - 2].id : null)} className="ml-auto flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded" style={{ backgroundColor: 'var(--theme-bg)'}}><ArrowLeft className="w-3 h-3" /> Back</button>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(currentClient.folders || []).filter(f => (f.parentId || null) === (currentFolderId || null)).map(folder => (
                      <div key={folder.id} className="group relative flex items-center gap-3 p-4 rounded-xl transition-all cursor-pointer border border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-bg)'}} onClick={() => setCurrentFolderId(folder.id)}>
                        <Folder className="w-8 h-8 text-blue-400 fill-blue-100 flex-shrink-0" />
                        {editingFolderId === folder.id && role === 'agency' ? (
                          <div className="flex-1 flex gap-1" onClick={e => e.stopPropagation()}>
                            <input type="text" autoFocus value={folderEditName} onChange={(e) => setFolderEditName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSaveFolderName(folder.id)} className="w-full px-2 py-1 text-sm rounded focus:outline-none border border-[var(--theme-input-border)]" />
                            <button onClick={() => handleSaveFolderName(folder.id)} className="p-1 text-blue-700 rounded"><Check className="w-4 h-4"/></button>
                          </div>
                        ) : (<span className="font-medium text-gray-800 truncate flex-1">{folder.name}</span>)}
                        {role === 'agency' && editingFolderId !== folder.id && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 md:opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity" onClick={e => e.stopPropagation()}>
                            <button onClick={() => { setEditingFolderId(folder.id); setFolderEditName(folder.name); }} className="p-1.5 text-gray-400 hover:text-gray-800 bg-white rounded shadow-sm"><Edit3 className="w-3 h-3" /></button>
                            <button onClick={() => handleDeleteFolder(folder.id)} className="p-1.5 text-gray-400 hover:text-red-600 bg-white rounded shadow-sm"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        )}
                      </div>
                    ))}

                    {currentClient.documents.filter(d => (d.folderId || null) === (currentFolderId || null)).map(doc => (
                      <div key={doc.id} className="group flex items-center gap-3 p-4 rounded-xl transition-all bg-white shadow-sm border border-[var(--theme-input-border)]">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${brandSettings.accentColor}15`, color: brandSettings.accentColor }}><FileText className="w-5 h-5" /></div>
                        <div className="flex-1 overflow-hidden">
                          <p className="font-medium text-sm text-gray-900 truncate">{doc.name}</p>
                          <p className="text-xs text-gray-500">{doc.size} • {doc.date}</p>
                        </div>
                        <div className="flex items-center gap-1 md:opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity">
                          {role === 'agency' && (
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteDocument(doc.id); }} className="p-2 text-gray-400 hover:text-red-600 rounded-lg transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={(e) => { e.stopPropagation(); handleDownloadFile(doc.url, doc.name); }} className="p-2 text-gray-400 hover:text-blue-600 rounded-lg transition-colors"><ExternalLink className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {((currentClient.folders || []).filter(f => (f.parentId || null) === (currentFolderId || null)).length === 0) && (currentClient.documents.filter(d => (d.folderId || null) === (currentFolderId || null)).length === 0) && (
                    <div className="text-center py-12 border-2 border-dashed rounded-xl mt-4" style={{ borderColor: 'var(--theme-bg)'}}>
                      <FolderPlus className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">{currentFolderId ? 'This folder is empty.' : 'No folders or documents yet.'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ========================================== */}
            {/* VIEW: CLIENTS DIRECTORY (Agency Only) */}
            {/* ========================================== */}
            {activeTab === 'clients' && role === 'agency' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Clients Directory ({filteredClientsList.length})</h2>
                    <p className="text-gray-500 text-sm mt-1">Manage all your agency's clients and filter by their information.</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 relative w-full sm:w-auto">
                    <button onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)} className={`flex-1 sm:flex-none justify-center flex items-center gap-2 px-4 py-2 bg-white rounded-lg font-medium shadow-sm text-sm transition-colors`} style={Object.keys(activeFilters).length > 0 ? { backgroundColor: `${brandSettings.accentColor}15`, color: brandSettings.accentColor } : { color: 'var(--theme-text)'}}>
                      <Filter className="w-4 h-4" /> Filters {Object.keys(activeFilters).length > 0 && `(${Object.values(activeFilters).flat().length})`}
                    </button>
                    <button onClick={handleAddNewClient} className="flex-1 sm:flex-none justify-center flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium shadow-sm text-sm" style={{ backgroundColor: brandSettings.accentColor }}><UserPlus className="w-4 h-4" /> Add New Client</button>

                    {isFilterMenuOpen && (
                      <div className="absolute top-full left-0 sm:left-auto sm:right-0 mt-2 w-full sm:w-72 bg-white shadow-xl rounded-xl z-50 overflow-hidden max-h-96 flex flex-col border border-[var(--theme-input-border)]">
                        <div className="p-3 flex justify-between items-center" style={{ backgroundColor: 'var(--theme-bg)'}}>
                          <span className="font-semibold text-gray-800 text-sm">Filter Clients</span>
                          {Object.keys(activeFilters).length > 0 && <button onClick={() => setActiveFilters({})} className="text-xs text-blue-600 hover:underline">Clear All</button>}
                        </div>
                        <div className="overflow-y-auto p-4 space-y-4">
                          {Object.entries(getAvailableFilters()).map(([category, values]) => (
                            <div key={category}>
                              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">{category}</h4>
                              <div className="space-y-1.5">
                                {values.map(val => (
                                  <label key={val} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer p-1 rounded transition-colors hover:opacity-80">
                                    <input type="checkbox" checked={(activeFilters[category] || []).includes(val)} onChange={() => toggleFilter(category, val)} className="rounded text-blue-600 focus:ring-blue-500 cursor-pointer" />
                                    {val}
                                  </label>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredClientsList.length === 0 ? (
                     <div className="col-span-1 md:col-span-2 text-center py-16 bg-white rounded-2xl border-dashed border-2" style={{ borderColor: 'var(--theme-bg)'}}><Users className="w-12 h-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-medium text-gray-900">No clients match filters</h3></div>
                  ) : (
                    filteredClientsList.map(clientData => (
                      <div key={clientData.profile.id} className="bg-white p-5 rounded-2xl shadow-sm border border-[var(--theme-input-border)] transition-all flex flex-col relative group">
                        <button onClick={() => handleDeleteClient(clientData.profile.id)} className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-red-600 rounded-lg md:opacity-0 group-hover:opacity-100 transition-all" title="Delete Client">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        
                        <div className="flex justify-between items-start mb-4 pr-8">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-gray-500 flex-shrink-0" style={{ backgroundColor: 'var(--theme-bg)' }}>{clientData.profile.name.substring(0,2).toUpperCase()}</div>
                            <div className="overflow-hidden">
                              <div className="flex flex-wrap items-center gap-2"><h3 className="font-bold text-gray-900 leading-tight truncate">{clientData.profile.name}</h3>{clientData.profile.returningClient && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7', color: isDarkMode ? '#fbbf24' : '#b45309' }}>Returning</span>}</div>
                              <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><User className="w-3 h-3"/> <span className="truncate">{clientData.profile.contact}</span></p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <span className="inline-block text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--theme-bg)', color: clientData.profile.projectStatus === 'New Onboarding' ? (isDarkMode ? '#818cf8' : '#4338ca') : clientData.profile.projectStatus === 'On going' ? (isDarkMode ? '#60a5fa' : '#1d4ed8') : (isDarkMode ? '#4ade80' : '#15803d') }}>{clientData.profile.projectStatus}</span>
                        </div>
                        
                        <div className="text-sm text-gray-600 space-y-1 mb-4 flex-1">
                          <p><strong className="font-medium text-gray-700">Plan:</strong> {clientData.profile.plan || 'Not set'}</p>
                          {(clientData.profile.customFields || []).slice(0, 2).map((cf, idx) => (cf.key && cf.value && <p key={idx}><strong className="font-medium text-gray-700">{cf.key}:</strong> <span className="truncate">{cf.value}</span></p>))}
                        </div>
                        <button onClick={() => { setActiveClientId(clientData.profile.id); setActiveTab('pipeline'); }} className="w-full py-2 rounded-lg text-sm font-medium transition-colors" style={{ backgroundColor: 'var(--theme-bg)' }}>View Workspace</button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* ========================================== */}
            {/* VIEW: CLIENT PROFILE */}
            {/* ========================================== */}
            {activeTab === 'profile' && activeClientId && (
              <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-sm border border-[var(--theme-input-border)] relative">
                
                {role === 'agency' && !isEditingProfile && (
                  <div className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-0 sm:absolute sm:top-8 sm:right-8 w-full sm:w-auto">
                    <button onClick={handleStartNewProject} className="flex-1 sm:flex-none justify-center flex items-center gap-2 px-4 py-2 text-white rounded-lg font-medium shadow-sm text-sm transition-colors hover:opacity-90" style={{ backgroundColor: brandSettings.accentColor }}><Plus className="w-4 h-4" /> Start New Project</button>
                    <button onClick={startEditingProfile} className="flex-1 sm:flex-none justify-center flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors text-sm border border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-bg)'}}><Edit3 className="w-4 h-4" /> Edit Profile</button>
                  </div>
                )}

                {role === 'client' && !isEditingProfile && (
                  <div className="flex flex-col sm:flex-row gap-3 mb-6 sm:mb-0 sm:absolute sm:top-8 sm:right-8 w-full sm:w-auto">
                    <button onClick={startEditingProfile} className="flex-1 sm:flex-none justify-center flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors text-sm border border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-bg)'}}><Edit3 className="w-4 h-4" /> Edit Contact Info</button>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8 pb-8 sm:mt-12" style={{ borderBottom: '2px solid var(--theme-bg)'}}>
                  <div className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold text-gray-400 flex-shrink-0 mx-auto sm:mx-0" style={{ backgroundColor: 'var(--theme-bg)' }}>
                    {isEditingProfile && role === 'agency' && profileFormData.name ? profileFormData.name.substring(0,2).toUpperCase() : currentClient.profile.name.substring(0,2).toUpperCase()}
                  </div>
                  <div className="flex-1 w-full text-center sm:text-left">
                    {isEditingProfile && role === 'agency' ? (
                      <div className="space-y-3 max-w-md mx-auto sm:mx-0 text-left">
                        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Company Name</label><input type="text" value={profileFormData.name || ''} onChange={e => setProfileFormData({...profileFormData, name: e.target.value})} className="w-full px-3 py-2 rounded-lg text-lg font-bold focus:outline-none border border-[var(--theme-input-border)]" style={{ focusRingColor: brandSettings.accentColor }} /></div>
                        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Primary Contact</label><input type="text" value={profileFormData.contact || ''} onChange={e => setProfileFormData({...profileFormData, contact: e.target.value})} className="w-full px-3 py-2 rounded-lg focus:outline-none border border-[var(--theme-input-border)]" /></div>
                      </div>
                    ) : isEditingProfile && role === 'client' ? (
                      <div className="space-y-3 max-w-md mx-auto sm:mx-0 text-left">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Company Name</p>
                          <h2 className="text-2xl font-bold text-gray-900 break-words">{currentClient.profile.name}</h2>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Primary Contact</label>
                          <input type="text" value={profileFormData.contact || ''} onChange={e => setProfileFormData({...profileFormData, contact: e.target.value})} className="w-full px-3 py-2 rounded-lg focus:outline-none border border-[var(--theme-input-border)]" />
                        </div>
                      </div>
                    ) : (
                      <>
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">{currentClient.profile.name}</h2>
                        <p className="text-gray-500 mt-1 flex items-center justify-center sm:justify-start gap-2"><User className="w-4 h-4" /> Primary Contact: {currentClient.profile.contact}</p>
                        
                        {role === 'agency' && (
                          <div className="mt-3 mb-1 inline-flex items-center gap-2 px-2 py-1 rounded border border-[var(--theme-input-border)] text-[10px] font-mono shadow-sm" style={{ backgroundColor: 'var(--theme-bg)' }}>
                            <span className="text-gray-400 uppercase font-bold tracking-wider">Database ID:</span> 
                            <span className="text-blue-500 font-bold select-all cursor-pointer" title="Click to copy">{currentClient.profile.id}</span>
                          </div>
                        )}

                        <div className="mt-2 flex flex-wrap items-center justify-center sm:justify-start gap-2">
                           <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--theme-bg)', color: currentClient.profile.projectStatus === 'New Onboarding' ? (isDarkMode ? '#818cf8' : '#4338ca') : currentClient.profile.projectStatus === 'On going' ? (isDarkMode ? '#60a5fa' : '#1d4ed8') : (isDarkMode ? '#4ade80' : '#15803d') }}>{currentClient.profile.projectStatus || 'On going'}</span>
                            {currentClient.profile.returningClient && <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full flex items-center gap-1" style={{ backgroundColor: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : '#fef3c7', color: isDarkMode ? '#fbbf24' : '#b45309' }}>Returning Client</span>}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2" style={{ borderBottom: '2px solid var(--theme-bg)'}}>Contact Details</h3>
                    {isEditingProfile ? (
                      <div className="space-y-4 max-w-md">
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Email Address</label>
                          {role === 'agency' ? (
                            <input type="email" value={profileFormData.email || ''} onChange={e => setProfileFormData({...profileFormData, email: e.target.value})} className="w-full px-3 py-2 rounded-lg focus:outline-none border border-[var(--theme-input-border)]" />
                          ) : (
                            <>
                              <p className="font-medium text-gray-900 break-all">{currentClient.profile.email || '-'}</p>
                              <p className="text-[10px] text-gray-400 mt-1 leading-tight">To change your login/invoice email address, please contact us directly.</p>
                            </>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Select Time Zone</label>
                          <select value={profileFormData.timezone || ''} onChange={e => setProfileFormData({...profileFormData, timezone: e.target.value})} className="w-full px-3 py-2 rounded-lg focus:outline-none text-sm border border-[var(--theme-input-border)]">
                            <option value="">Select timezone...</option>
                            {utcOffsets.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Best Time to Contact</label>
                          <div className="flex items-center gap-2">
                            <select value={profileFormData.contactStart || ''} onChange={e => setProfileFormData({...profileFormData, contactStart: e.target.value})} className="flex-1 px-3 py-2 rounded-lg focus:outline-none text-sm border border-[var(--theme-input-border)]">
                              <option value="">Start</option>
                              {hoursList.map(h => <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                            </select>
                            <span className="text-xs font-bold text-gray-400 tracking-wider">TO</span>
                            <select value={profileFormData.contactEnd || ''} onChange={e => setProfileFormData({...profileFormData, contactEnd: e.target.value})} className="flex-1 px-3 py-2 rounded-lg focus:outline-none text-sm border border-[var(--theme-input-border)]">
                              <option value="">End</option>
                              {hoursList.map(h => <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                            </select>
                          </div>
                        </div>
                        {role === 'agency' && (
                          <div className="pt-2"><label className="flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer"><input type="checkbox" checked={profileFormData.returningClient || false} onChange={(e) => setProfileFormData({...profileFormData, returningClient: e.target.checked})} className="rounded text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer"/> Returning Client</label></div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div><p className="text-sm text-gray-500 mb-1">Email Address</p><p className="font-medium text-gray-900 break-all">{currentClient.profile.email || '-'}</p></div>
                        <div><p className="text-sm text-gray-500 mb-1">Time Zone</p><p className="font-medium text-gray-900">{utcOffsets.find(t => t.value.toString() === currentClient.profile.timezone?.toString())?.label || '-'}</p></div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Best Time to Contact</p>
                          <p className="font-medium text-gray-900 flex flex-col sm:flex-row sm:items-center gap-2">
                            <span>{currentClient.profile.contactStart != null && currentClient.profile.contactEnd != null && currentClient.profile.contactStart !== "" && currentClient.profile.contactEnd !== "" ? `${String(currentClient.profile.contactStart).padStart(2,'0')}:00 to ${String(currentClient.profile.contactEnd).padStart(2,'0')}:00` : '-'}</span>
                            {role === 'agency' && currentClient.profile.timezone && currentClient.profile.contactStart !== "" && currentClient.profile.contactEnd !== "" && (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 px-2 py-0.5 rounded border border-[var(--theme-input-border)]" style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)' }}>
                                <Clock className="w-3 h-3" /> JST: {convertToJST(currentClient.profile.contactStart, currentClient.profile.timezone)} to {convertToJST(currentClient.profile.contactEnd, currentClient.profile.timezone)}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4 pb-2" style={{ borderBottom: '2px solid var(--theme-bg)'}}>Account Info</h3>
                    {isEditingProfile && role === 'agency' ? (
                      <div className="space-y-4 max-w-md">
                        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Active Project</label><input type="text" value={profileFormData.plan || ''} onChange={e => setProfileFormData({...profileFormData, plan: e.target.value})} className="w-full px-3 py-2 rounded-lg focus:outline-none border border-[var(--theme-input-border)]" /></div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 mb-1">Client Since</label>
                          <input type="date" value={profileFormData.joined || ''} onChange={e => setProfileFormData({...profileFormData, joined: e.target.value})} className="w-full px-3 py-2 rounded-lg focus:outline-none border border-[var(--theme-input-border)]" />
                        </div>
                        <div><label className="block text-xs font-semibold text-gray-500 mb-1">Project Status</label><select value={profileFormData.projectStatus || 'On going'} onChange={e => setProfileFormData({...profileFormData, projectStatus: e.target.value})} className="w-full px-3 py-2 rounded-lg focus:outline-none border border-[var(--theme-input-border)]"><option value="New Onboarding">New Onboarding</option><option value="On going">On going</option><option value="Finished">Finished</option></select></div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div><p className="text-sm text-gray-500 mb-1">Active Project</p>{currentClient.profile.plan ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: `${brandSettings.accentColor}15`, color: brandSettings.accentColor }}>{currentClient.profile.plan}</span> : <span className="text-gray-500">-</span>}</div>
                        <div><p className="text-sm text-gray-500 mb-1">Client Since</p><p className="font-medium text-gray-900">{formatDisplayDate(currentClient.profile.joined)}</p></div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8">
                  <div className="flex justify-between items-end mb-4 pb-2" style={{ borderBottom: '2px solid var(--theme-bg)'}}>
                    <h3 className="text-lg font-bold text-gray-800">Additional Information</h3>
                    {isEditingProfile && role === 'agency' && <button onClick={handleAddCustomField} className="flex items-center gap-1 text-xs px-2 py-1 text-gray-600 rounded transition-colors" style={{ backgroundColor: 'var(--theme-bg)'}}><Plus className="w-3 h-3" /> Add Field</button>}
                  </div>
                  {isEditingProfile && role === 'agency' ? (
                    <div className="space-y-3 max-w-2xl">
                      {(!profileFormData.customFields || profileFormData.customFields.length === 0) && <p className="text-sm text-gray-500 italic">No additional information added.</p>}
                      {(profileFormData.customFields || []).map((field, index) => (
                        <div key={index} className="flex flex-col sm:flex-row items-start gap-3 p-3 rounded-lg border border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-bg)'}}>
                          <div className="w-full sm:flex-1"><label className="block text-xs font-semibold text-gray-500 mb-1">Information Label</label><input type="text" value={field.key} onChange={e => handleCustomFieldChange(index, 'key', e.target.value)} className="w-full px-3 py-2 rounded focus:outline-none text-sm border border-[var(--theme-input-border)]" placeholder="Key" /></div>
                          <div className="w-full sm:flex-[2]">
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Value</label>
                            <div className="flex gap-2">
                              <input type="text" value={field.value} onChange={e => handleCustomFieldChange(index, 'value', e.target.value)} className="w-full px-3 py-2 rounded focus:outline-none text-sm border border-[var(--theme-input-border)]" placeholder="Value" />
                              <button onClick={() => handleRemoveCustomField(index)} className="p-2 text-gray-400 hover:text-red-500 bg-white rounded transition-colors border border-[var(--theme-input-border)]"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                      {(!currentClient.profile.customFields || currentClient.profile.customFields.length === 0) ? <p className="text-sm text-gray-500 col-span-full">No additional information available.</p> : (
                        currentClient.profile.customFields.map((field, index) => field.key && field.value && <div key={index}><p className="text-sm text-gray-500 mb-1">{field.key}</p><p className="font-medium text-gray-900 break-words">{field.value}</p></div>)
                      )}
                    </div>
                  )}
                </div>

                {isEditingProfile && (
                  <div className="mt-8 pt-6 flex flex-col sm:flex-row gap-3" style={{ borderTop: '2px solid var(--theme-bg)'}}>
                    <button onClick={saveProfile} className="w-full sm:w-auto flex justify-center items-center gap-2 px-6 py-2 text-white rounded-lg font-medium shadow-sm" style={{ backgroundColor: brandSettings.accentColor }}><Save className="w-4 h-4" /> Save Updates</button>
                    <button onClick={() => setIsEditingProfile(false)} className="w-full sm:w-auto px-6 py-2 bg-white text-gray-700 rounded-lg font-medium transition-colors" style={{ border: '1px solid var(--theme-input-border)'}}>Cancel</button>
                    {role === 'agency' && (
                      <button onClick={() => handleDeleteClient(currentClient.profile.id)} className="w-full sm:w-auto sm:ml-auto px-4 py-2 text-red-600 rounded-lg font-medium transition-colors flex items-center justify-center gap-2" style={{ backgroundColor: 'rgba(225, 29, 72, 0.1)'}}><Trash2 className="w-4 h-4" /> Delete Client</button>
                    )}
                  </div>
                )}
                
                {/* Project History List */}
                {!isEditingProfile && (
                  <div className="mt-12 pt-8" style={{ borderTop: '2px solid var(--theme-bg)'}}>
                    <div className="flex items-center gap-2 mb-6"><History className="w-5 h-5 text-gray-500" /><h3 className="text-lg font-bold text-gray-800">Project History</h3></div>
                    {(!currentClient.projectHistory || currentClient.projectHistory.length === 0) ? (
                      <div className="text-center py-8 rounded-xl border-dashed border-2" style={{ borderColor: 'var(--theme-bg)'}}><p className="text-sm text-gray-500">No past projects available for this client.</p></div>
                    ) : (
                      <div className="space-y-4">
                        {currentClient.projectHistory.map((hist) => (
                          <div key={hist.id} className="rounded-xl overflow-hidden bg-white shadow-sm border border-[var(--theme-input-border)]">
                            <button onClick={() => setExpandedHistoryId(expandedHistoryId === hist.id ? null : hist.id)} className="w-full px-4 sm:px-6 py-4 flex justify-between items-center transition-colors hover:opacity-80">
                              <div className="text-left">
                                <h4 className="font-bold text-gray-900 text-base sm:text-lg">{hist.projectName}</h4>
                                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-green-500" /> Completed: {hist.completedDate}</p>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="hidden sm:inline-block text-xs font-semibold px-2 py-1 text-gray-600 rounded border border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-bg)'}}>Locked / Read-Only</span>
                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${expandedHistoryId === hist.id ? 'rotate-180' : ''}`} />
                              </div>
                            </button>
                            {expandedHistoryId === hist.id && (
                              <div className="px-4 sm:px-6 py-6 border-t border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-bg)'}}>
                                <div className="space-y-6">
                                  {hist.pipeline.map((step, index) => (
                                    <div key={step.id} className="relative flex flex-col md:flex-row items-start gap-4">
                                      <div className="hidden md:flex flex-col items-center z-10 pt-1">
                                        <CheckCircle2 className="w-6 h-6 text-green-500 bg-white rounded-full" />
                                        {index !== hist.pipeline.length - 1 && <div className="w-0.5 h-full my-1 bg-green-400"></div>}
                                      </div>
                                      <div className="flex-1 bg-white rounded-xl p-5 shadow-sm opacity-90 w-full overflow-hidden border border-[var(--theme-input-border)]">
                                        <div className="flex justify-between items-start mb-2">
                                          <div>
                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Phase {index + 1}</span>
                                              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: isDarkMode ? 'rgba(34, 197, 94, 0.15)' : '#dcfce7', color: isDarkMode ? '#4ade80' : '#166534' }}>Completed</span>
                                              {step.approved && <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.15)' : '#dbeafe', color: isDarkMode ? '#60a5fa' : '#1d4ed8' }}>Client Approved</span>}
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-800">{step.title}</h3>
                                          </div>
                                          <span className="text-xs text-gray-400 whitespace-nowrap ml-2">{step.date}</span>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-4">{step.description}</p>
                                        
                                        {step.deliverables && step.deliverables.length > 0 && (
                                          <div className="mb-4">
                                            <h4 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Final Deliverables</h4>
                                            <div className="flex flex-wrap gap-2">
                                              {step.deliverables.map((file, i) => <div key={i} className="flex items-center gap-2 px-2 py-1 rounded text-xs text-gray-600 border border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-bg)'}}><FileText className="w-3 h-3 text-gray-400 flex-shrink-0" /> <span className="truncate">{file.name}</span> <button onClick={(e) => { e.stopPropagation(); handleDownloadFile(file.url, file.name); }} className="hover:text-blue-500 ml-1"><ExternalLink className="w-3 h-3"/></button></div>)}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {(step.payments || []).map((pay, pIdx) => (
                                          <div key={pay.id} className="mt-3 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border border-[var(--theme-input-border)]" style={{ backgroundColor: pay.status === 'paid' ? (isDarkMode ? 'rgba(34, 197, 94, 0.1)' : '#dcfce7') : (isDarkMode ? 'rgba(225, 29, 72, 0.1)' : '#ffe4e6') }}>
                                            <div className="flex items-center gap-2"><CreditCard className={`w-4 h-4 ${pay.status === 'paid' ? (isDarkMode ? 'text-green-400' : 'text-green-600') : (isDarkMode ? 'text-rose-400' : 'text-rose-600')}`} /><h4 className="text-sm font-bold text-gray-900">Invoice {step.payments?.length > 1 ? `#${pIdx + 1}` : ''}: ${pay.amount}</h4></div>
                                            <div className="flex items-center gap-3">
                                              <span className="uppercase tracking-wider text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: pay.status === 'paid' ? (isDarkMode ? 'rgba(34, 197, 94, 0.15)' : '#dcfce7') : (isDarkMode ? 'rgba(225, 29, 72, 0.15)' : '#ffe4e6'), color: pay.status === 'paid' ? (isDarkMode ? '#4ade80' : '#166534') : (isDarkMode ? '#fb7185' : '#be123c') }}>{pay.status}</span>
                                              {role === 'client' && pay.link && (
                                                <a href={pay.link} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"><ExternalLink className="w-3 h-3" /> Receipt</a>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ========================================== */}
            {/* VIEW: PORTAL SETTINGS */}
            {/* ========================================== */}
            {activeTab === 'settings' && role === 'agency' && (
              <div className="bg-white rounded-2xl p-5 sm:p-8 shadow-sm border border-[var(--theme-input-border)]">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Portal Customization</h2>
                <p className="text-gray-500 mb-8">Design the client portal to match your agency's exact branding rules.</p>
                <div className="space-y-8 max-w-2xl">
                  <div className="pb-6" style={{ borderBottom: '2px solid var(--theme-bg)'}}>
                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">Brand Identity</h3>
                    <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                      <div className="w-full sm:flex-[2]"><label className="block text-sm font-medium text-gray-700 mb-1">Agency Name</label><input type="text" value={brandSettings.name} onChange={(e) => updateBrand('name', e.target.value)} className="w-full px-4 py-2 rounded-lg focus:outline-none" /></div>
                      <div className="w-full sm:flex-[3]">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Upload Logo</label>
                        <div className="flex items-center gap-4">
                          <label className="cursor-pointer flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 transition-colors w-full sm:w-auto border border-[var(--theme-input-border)]" style={{ backgroundColor: 'var(--theme-bg)'}}><input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} /><ImageIcon className="w-4 h-4" /> Choose File</label>
                          {brandSettings.logo && <div className="relative"><img src={brandSettings.logo} alt="Preview" className="h-10 object-contain rounded p-1" /><button onClick={() => updateBrand('logo', null)} className="absolute -top-2 -right-2 bg-red-100 text-red-600 rounded-full p-0.5"><X className="w-3 h-3"/></button></div>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="pb-6" style={{ borderBottom: '2px solid var(--theme-bg)'}}>
                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4">Color Palette</h3>
                    <div className="max-w-xs">
                      <label className="block text-xs font-medium text-gray-700 mb-2">Primary Accent Color</label>
                      <div className="flex gap-2">
                         <input type="color" value={brandSettings.accentColor} onChange={(e) => updateBrand('accentColor', e.target.value)} className="h-10 w-full rounded cursor-pointer border-0 p-0" />
                         <input type="text" value={brandSettings.accentColor} onChange={(e) => updateBrand('accentColor', e.target.value)} className="w-full px-2 py-1 rounded uppercase font-mono text-xs text-center border border-[var(--theme-input-border)]" />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">App & Panel backgrounds are automatically set to your light/dark theme preference globally.</p>
                    </div>
                  </div>
                  
                  <div className="pt-4 flex justify-end">
                    <button onClick={() => showAlert("Settings Saved", "Your portal customizations have been successfully updated.")} className="w-full sm:w-auto flex justify-center items-center gap-2 px-6 py-2 text-white rounded-lg font-medium shadow-sm transition-colors hover:opacity-90" style={{ backgroundColor: brandSettings.accentColor }}>
                      <Save className="w-4 h-4" /> Save Updates
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* NEW VIEW: CLIENT SECURITY & SETTINGS */}
            {activeTab === 'settings' && role === 'client' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Security & Settings</h2>
                  <p className="text-gray-500 mb-8">Manage your account security and view portal policies.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Change Password Card */}
                  <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border" style={{ borderColor: 'var(--theme-input-border)' }}>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--theme-bg)' }}>
                        <Lock className="w-5 h-5 text-gray-700" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-800">Change Password</h3>
                    </div>
                    
                    <form onSubmit={handleClientPasswordChange} className="space-y-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Current Password</label>
                        <input type="password" value={clientPasswordData.current} onChange={e => setClientPasswordData({...clientPasswordData, current: e.target.value})} className="w-full px-4 py-2.5 rounded-lg focus:outline-none transition-all" placeholder="••••••••" required />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">New Password</label>
                        <input type="password" value={clientPasswordData.new} onChange={e => setClientPasswordData({...clientPasswordData, new: e.target.value})} className="w-full px-4 py-2.5 rounded-lg focus:outline-none transition-all" placeholder="At least 6 characters" required />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 mb-1">Confirm New Password</label>
                        <input type="password" value={clientPasswordData.confirm} onChange={e => setClientPasswordData({...clientPasswordData, confirm: e.target.value})} className="w-full px-4 py-2.5 rounded-lg focus:outline-none transition-all" placeholder="At least 6 characters" required />
                      </div>
                      <button type="submit" className="w-full mt-2 py-2.5 rounded-lg text-white font-bold transition-transform hover:opacity-90 active:scale-95 shadow-sm" style={{ backgroundColor: brandSettings.accentColor }}>
                        Update Password
                      </button>
                    </form>
                  </div>

                  <div className="space-y-6">
                    {/* Support Card */}
                    <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border" style={{ borderColor: 'var(--theme-input-border)' }}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--theme-bg)' }}>
                          <User className="w-5 h-5 text-gray-700" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-800">Support & Contact</h3>
                      </div>
                      <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                        Need help accessing your files, submitting feedback, or have questions about our process? Our team is ready to assist you.
                      </p>
                      <p className="text-sm font-medium text-gray-800 mb-2">Reach us directly at:</p>
                      <a href="mailto:support@sushiscribe.com" className="inline-flex items-center gap-2 font-bold hover:underline text-base sm:text-lg break-all" style={{ color: brandSettings.accentColor }}>
                        support@sushiscribe.com <ExternalLink className="w-4 h-4 flex-shrink-0" />
                      </a>
                    </div>

                    {/* Security Policy Card */}
                    <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-sm border" style={{ borderColor: 'var(--theme-input-border)' }}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--theme-bg)' }}>
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-800">Security Policies</h3>
                      </div>
                      <ul className="space-y-4 text-sm text-gray-600">
                        <li className="flex items-start gap-2.5">
                          <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: brandSettings.accentColor }}></div>
                          <div>
                            <strong className="text-gray-800 block mb-0.5">Ironclad Asset Protection</strong>
                            Your creative files, project details, and feedback are locked down using industry-leading encryption. We guard your data with the same care we give to our art.
                          </div>
                        </li>
                        <li className="flex items-start gap-2.5">
                          <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: brandSettings.accentColor }}></div>
                          <div>
                            <strong className="text-gray-800 block mb-0.5">Secure, Hands-Off Billing</strong>
                            All invoices are processed through trusted external banking gateways. Your sensitive payment data never touches our servers.
                          </div>
                        </li>
                        <li className="flex items-start gap-2.5">
                          <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: brandSettings.accentColor }}></div>
                          <div>
                            <strong className="text-gray-800 block mb-0.5">Exclusive Workspace Access</strong>
                            Your portal is a private environment. We actively monitor logins to ensure your project data remains strictly confidential and entirely yours.
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>

                </div>
              </div>
            )}

          </div>
        </div>
      </main>
    </div>
  );
}